import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { testService } from '../../../services/testService';
import { useAssessmentContext } from '../AssessmentContext';
import AssessmentPopup from '../dashboard/AssessmentPopup';
import toast from 'react-hot-toast';
import QuestionBankManager from '../QuestionBank/QuestionBankManager';
import TemplateBuilder from '../Templates/TemplateBuilder';
import GroupsInlineSearchField from '../dashboard/GroupsInlineSearchField';
import GroupsPickerDropdownPortal from '../dashboard/GroupsPickerDropdownPortal';
import { GroupsPickerMemberRow, GroupsPickerPreviewRow } from '../dashboard/GroupsPickerDropdownPanel';
import GroupsMemberPreviewPopover from '../dashboard/GroupsMemberPreviewPopover';
import RoleSelectionSummary from '../dashboard/RoleSelectionSummary';
import { resolveAssessmentRoleNames } from '../dashboard/assessmentRoleUtils';
import { 
  FiCheck, 
  FiFileText, 
  FiLayers, 
  FiUsers, 
  FiTrash2, 
  FiPlus, 
  FiArrowRight, 
  FiArrowLeft, 
  FiSave,
  FiVideo,
  FiAlertTriangle,
  FiPlayCircle,
  FiClock,
  FiCheckCircle,
  FiLogOut,
  FiUploadCloud,
  FiSkipBack,
  FiMaximize,
  FiChevronDown,
  FiCalendar,
  FiSearch,
  FiEdit,
  FiEye
} from 'react-icons/fi';

const STEPS = [
  { id: 'create', label: 'Create Test', path: '/workflow/create' },
  { id: 'question-bank', label: 'Question Bank', path: '/workflow/question-bank' },
  { id: 'template', label: 'Template', path: '/workflow/template' },
  { id: 'groups', label: 'Groups & Assign', path: '/workflow/groups' },
  { id: 'publish', label: 'Save', path: '/workflow/publish' }
];

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.includes('/')) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const getDefaultNewGroup = (template) => ({
  pack_name: '',
  template_id: template?.id || '',
  template_name: template?.name || '',
  user_role: [],
  candidates: [],
  scheduled_date: new Date().toISOString().split('T')[0],
  start_time: '10:00',
  end_time: '11:00',
  duration: 60
});

const normalizePendingGroupForm = (form = {}) => ({
  newGroup: { ...getDefaultNewGroup(), ...(form.newGroup || {}) },
  editingGroupId: form.editingGroupId || null,
  showInputRow: form.showInputRow !== false,
  roleSearchTerm: form.roleSearchTerm || '',
  candidateSearchTerm: form.candidateSearchTerm || '',
});

const groupFormHasDraftContent = (form, _showRow, editingId) => {
  const group = form || {};
  return Boolean(
    editingId ||
    String(group.pack_name || '').trim() ||
    String(group.template_id || '').trim() ||
    (Array.isArray(group.user_role) && group.user_role.length > 0) ||
    (Array.isArray(group.candidates) && group.candidates.length > 0)
  );
};

const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '';
  try {
    if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) {
      return timeStr;
    }
    const parts = timeStr.split(':');
    let hours = parseInt(parts[0], 10);
    let minutes = parseInt(parts[1] || '0', 10);
    if (isNaN(hours) || isNaN(minutes)) return timeStr;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    let displayHour = hours % 12;
    displayHour = displayHour ? displayHour : 12;
    const hPad = String(displayHour).padStart(2, '0');
    const mPad = String(minutes).padStart(2, '0');
    return `${hPad}:${mPad} ${ampm}`;
  } catch (e) {
    return timeStr;
  }
};

const getInitialCompletedSteps = (draft) => {
  if (!draft) return [];
  const completed = [];
  if (draft.name && draft.category && draft.type && draft.instructions && (!draft.videoEnabled || (draft.videoFiles && draft.videoFiles.length > 0))) {
    completed.push('create');
  }
  if (draft.questionBank && draft.questionBank.name && getQuestionBankCount(draft.questionBank) > 0) {
    completed.push('question-bank');
  }
  if (draft.template && draft.template.name && draft.template.sections && draft.template.sections.length > 0) {
    completed.push('template');
  }
  if (draft.groups && draft.groups.length > 0) {
    completed.push('groups');
  }
  return completed;
};

const getEarliestIncompleteStep = (draft) => {
  if (!draft) return 'create';
  const stepOrder = ['create', 'question-bank', 'template', 'groups', 'publish'];
  const completed = (draft.completedSteps && draft.completedSteps.length > 0) 
    ? draft.completedSteps 
    : getInitialCompletedSteps(draft);
  const incomplete = stepOrder.find(s => !completed.includes(s));
  return incomplete || 'publish';
};

const generateRandomCode = () => `TST-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

const ReviewDetailRow = ({ label, value, multiline = false }) => (
  <div className="assess-wizard-review-row">
    <span className="assess-wizard-review-label">{label}</span>
    <span className={`assess-wizard-review-value${multiline ? ' assess-wizard-review-value--multiline' : ''}`}>
      {value ?? '—'}
    </span>
  </div>
);

const getQuestionBankCount = (questionBank) => {
  if (!questionBank) return 0;
  if (Array.isArray(questionBank.questions)) return questionBank.questions.length;
  return Number(questionBank.questionCount || 0);
};

const normalizeTemplateSections = (sections = []) =>
  (Array.isArray(sections) ? sections : []).map((sec) => {
    const count = Number(sec.question_count ?? sec.count ?? sec.num_questions ?? 0);
    const marks = Number(sec.marks_each ?? sec.marks ?? sec.marks_per_question ?? 0);
    const name = sec.section_name || sec.name || 'Section';
    return {
      ...sec,
      name,
      section_name: name,
      count,
      marks,
      question_count: count,
      marks_each: marks,
      num_questions: count,
      marks_per_question: marks,
    };
  });

const getTemplatePassMark = (template) => {
  if (!template) return 0;
  const raw = template.passMark ?? template.pass_mark;
  if (raw === undefined || raw === null || raw === '') return 0;
  return Number(raw);
};

const getTemplateTotalMarks = (template) => {
  if (!template) return 0;
  const sectionTotal = normalizeTemplateSections(template.sections).reduce(
    (sum, sec) => sum + (sec.count * sec.marks),
    0
  );
  if (sectionTotal > 0) return sectionTotal;
  const stored = template.totalMarks ?? template.total_marks;
  if (stored === undefined || stored === null || stored === '') return 0;
  return Number(stored);
};

const buildDraftTemplateFromApi = (apiTemplate, existingTemplate = {}) => ({
  ...existingTemplate,
  id: apiTemplate.id,
  name: apiTemplate.name || existingTemplate.name || '',
  questionBank: apiTemplate.bank || existingTemplate.questionBank || '',
  passMark: getTemplatePassMark(apiTemplate) || getTemplatePassMark(existingTemplate),
  sections: normalizeTemplateSections(
    (apiTemplate.sections && apiTemplate.sections.length > 0)
      ? apiTemplate.sections
      : (existingTemplate.sections || [])
  ),
  questions: apiTemplate.questions || existingTemplate.questions || [],
});

const createStorageSafeDraft = (draft, mode = 'default') => {
  const next = { ...draft };

  if (next.template) {
    next.template = {
      ...next.template,
      questionCount: Array.isArray(next.template.questions) ? next.template.questions.length : Number(next.template.questionCount || 0),
      questions: mode === 'aggressive' ? [] : undefined
    };
  }

  if (mode === 'aggressive' && next.questionBank) {
    next.questionBank = {
      ...next.questionBank,
      questionCount: getQuestionBankCount(next.questionBank),
      questions: []
    };
  }

  return next;
};

const TimePicker12Hour = ({ value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const dropdownPanelRef = useRef(null);
  const [position, setPosition] = useState(null);

  let initialHour = 10;
  let initialMinute = 0;
  let initialAmpm = 'AM';

  if (value) {
    const parts = value.split(':');
    if (parts.length >= 2) {
      let h = parseInt(parts[0], 10);
      initialMinute = parseInt(parts[1], 10);
      if (isNaN(h)) h = 10;
      if (isNaN(initialMinute)) initialMinute = 0;

      initialAmpm = h >= 12 ? 'PM' : 'AM';
      initialHour = h % 12;
      if (initialHour === 0) initialHour = 12;
    }
  }

  const syncPosition = useCallback(() => {
    if (!dropdownRef.current) return null;
    const rect = dropdownRef.current.getBoundingClientRect();
    const width = 160; // w-[160px]
    let left = rect.left;
    const viewportTop = rect.bottom + 4;
    if (left + width > window.innerWidth - 8) {
      left = window.innerWidth - width - 8;
    }
    if (left < 8) {
      left = 8;
    }
    return { top: viewportTop, left };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const next = syncPosition();
    if (next) setPosition(next);
  }, [isOpen, syncPosition]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    const handleScroll = (event) => {
      if (dropdownPanelRef.current && dropdownPanelRef.current.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };
    const handleResize = () => {
      const next = syncPosition();
      if (next) setPosition(next);
    };
    const next = syncPosition();
    if (next) setPosition(next);

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, syncPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = dropdownRef.current && dropdownRef.current.contains(event.target);
      const inPanel = dropdownPanelRef.current && dropdownPanelRef.current.contains(event.target);
      if (!inAnchor && !inPanel) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (hourRef.current) {
          const selected = hourRef.current.querySelector('.selected-time-item');
          if (selected) {
            selected.scrollIntoView({ block: 'center', behavior: 'auto' });
          }
        }
        if (minuteRef.current) {
          const selected = minuteRef.current.querySelector('.selected-time-item');
          if (selected) {
            selected.scrollIntoView({ block: 'center', behavior: 'auto' });
          }
        }
      }, 30);
    }
  }, [isOpen]);

  const handleValChange = (newH, newM, newAmpm) => {
    let h24 = newH;
    if (newAmpm === 'PM' && h24 < 12) h24 += 12;
    if (newAmpm === 'AM' && h24 === 12) h24 = 0;

    const hStr = String(h24).padStart(2, '0');
    const mStr = String(newM).padStart(2, '0');
    onChange(`${hStr}:${mStr}`);
  };

  const formattedHour = String(initialHour).padStart(2, '0');
  const formattedMinute = String(initialMinute).padStart(2, '0');

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between bg-[#fcfdfe] border rounded-[0.875rem] px-3 py-1.5 cursor-pointer shadow-sm select-none transition-all w-full hover:bg-slate-50 border-slate-100 focus:outline-none ${
          isOpen ? 'border-red-500 ring-2 ring-red-100' : ''
        } ${disabled ? 'opacity-80 cursor-default pointer-events-none' : ''}`}
      >
        <span className="text-xs font-bold text-slate-700 tracking-wide">
          {formattedHour}:{formattedMinute} {initialAmpm}
        </span>
        <svg className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {isOpen && position && createPortal(
        <div 
          ref={dropdownPanelRef}
          className="bg-white border border-slate-200 rounded-2xl shadow-xl p-2.5 flex flex-col gap-2 w-[160px]"
          style={{ 
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 999999
          }}
        >
          <style>{`
            .custom-timepicker-scroll::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          
          {/* Selected Display Row */}
          <div className="grid grid-cols-3 gap-1 pb-2 border-b border-slate-100">
            <div className="h-8 rounded border-2 border-slate-800 bg-slate-200/80 text-slate-800 font-extrabold flex items-center justify-center text-xs select-none">
              {formattedHour}
            </div>
            <div className="h-8 rounded bg-slate-200/80 text-slate-800 font-extrabold flex items-center justify-center text-xs select-none">
              {formattedMinute}
            </div>
            <div className="h-8 rounded bg-slate-200/80 text-slate-800 font-extrabold flex items-center justify-center text-xs select-none">
              {initialAmpm}
            </div>
          </div>

          {/* Picker Columns */}
          <div className="flex justify-between gap-1 h-[160px]">
            {/* Hour Column */}
            <div 
              ref={hourRef}
              className="custom-timepicker-scroll flex-1 flex flex-col gap-1 overflow-y-auto scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => {
                const hVal = String(h).padStart(2, '0');
                const isSelected = h === initialHour;
                return (
                  <button
                    type="button"
                    key={h}
                    onClick={() => handleValChange(h, initialMinute, initialAmpm)}
                    className={`w-full py-1 text-center text-xs font-bold rounded hover:bg-slate-100 transition-colors cursor-pointer border-0 outline-none ${
                      isSelected ? 'selected-time-item text-red-600 bg-red-50/50' : 'text-slate-600'
                    }`}
                  >
                    {hVal}
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="w-px bg-slate-100 self-stretch" />

            {/* Minute Column */}
            <div 
              ref={minuteRef}
              className="custom-timepicker-scroll flex-1 flex flex-col gap-1 overflow-y-auto scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {Array.from({ length: 60 }, (_, i) => i).map((m) => {
                const mVal = String(m).padStart(2, '0');
                const isSelected = m === initialMinute;
                return (
                  <button
                    type="button"
                    key={m}
                    onClick={() => handleValChange(initialHour, m, initialAmpm)}
                    className={`w-full py-1 text-center text-xs font-bold rounded hover:bg-slate-100 transition-colors cursor-pointer border-0 outline-none ${
                      isSelected ? 'selected-time-item text-red-600 bg-red-50/50' : 'text-slate-600'
                    }`}
                  >
                    {mVal}
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="w-px bg-slate-100 self-stretch" />

            {/* AM/PM Column */}
            <div 
              className="custom-timepicker-scroll flex-1 flex flex-col gap-1 overflow-y-auto"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {['AM', 'PM'].map((ampm) => {
                const isSelected = ampm === initialAmpm;
                return (
                  <button
                    type="button"
                    key={ampm}
                    onClick={() => handleValChange(initialHour, initialMinute, ampm)}
                    className={`w-full py-1.5 text-center text-xs font-bold rounded hover:bg-slate-100 transition-colors cursor-pointer border-0 outline-none ${
                      isSelected ? 'text-red-600 bg-red-50/50' : 'text-slate-600'
                    }`}
                  >
                    {ampm}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export const WorkflowWizard = () => {
  const { step } = useParams();
  const navigate = useNavigate();
  const { navigateToModule } = useAssessmentContext();
  const fileInputRef = useRef(null);

  // Wizard state
  const [draft, setDraft] = useState({
    id: generateRandomCode(),
    name: '',
    category: 'Internal',
    type: '',
    instructions: '',
    videoEnabled: false,
    videoFiles: [],
    navigationControl: false,
    autoSubmit: false,
    fullscreenMode: false,
    tabSwitchWarning: false,
    status: 'Draft',
    currentStep: 'create',
    completedSteps: [],
    questionBank: null,
    template: null,
    groups: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });

  // UI / popups state
  const [viewingQuestion, setViewingQuestion] = useState(null);
  const [popupConfig, setPopupConfig] = useState({ isOpen: false });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletedPackIds, setDeletedPackIds] = useState([]);

  // Categories list state
  const [categoriesList, setCategoriesList] = useState([]);

  useEffect(() => {
    testService.getCategories()
      .then(res => {
        if (res.status === 'success' && Array.isArray(res.categories)) {
          const allowed = ['Internal', 'External', 'HR Recruitment-Fresher'];
          const filtered = res.categories.filter(cat => allowed.includes(cat.name));
          setCategoriesList(filtered);
        }
      })
      .catch(err => {
        console.error('Failed to load categories', err);
      });
  }, []);

  const isHrCategory = (categoryName) => {
    const matched = categoriesList.find(c => String(c.name).trim().toLowerCase() === String(categoryName || '').trim().toLowerCase());
    if (matched) {
      return Number(matched.is_hr_type) === 1;
    }
    const cat = String(categoryName || '').trim();
    return cat === 'HR Recruitment-Fresher' || cat === 'External';
  };

  // Step 2: Question Banks state
  const [questionBanks, setQuestionBanks] = useState([]);
  const [loadingQB, setLoadingQB] = useState(false);
  const [selectedQB, setSelectedQB] = useState(null);
  const [qbSearchQuery, setQbSearchQuery] = useState('');

  // Step 3: Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');

  // Step 4: Groups state
  const [showInputRow, setShowInputRow] = useState(!draft?.groups || draft.groups.length === 0);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [resolvedCandidatesMap, setResolvedCandidatesMap] = useState({});
  const [loadingExpandedGroupId, setLoadingExpandedGroupId] = useState(null);
  const [showRolesDropdown, setShowRolesDropdown] = useState(false);
  const [showSelectedRolesOnly, setShowSelectedRolesOnly] = useState(false);
  const [showCandidatesDropdown, setShowCandidatesDropdown] = useState(false);
  const [showSelectedCandidatesOnly, setShowSelectedCandidatesOnly] = useState(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [jobRoles, setJobRoles] = useState([]);
  const [collegeTemplates, setCollegeTemplates] = useState([]);
  const [candidatesList, setCandidatesList] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [newGroup, setNewGroup] = useState(getDefaultNewGroup);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [editingGroupId, setEditingGroupId] = useState(null);

  const isRestoringGroupFormRef = useRef(false);
  const draftHydratedRef = useRef(false);
  const templatesLoadedRef = useRef(false);
  const templateWizardSteps = ['question-bank', 'template', 'groups', 'publish'];
  const [templatePanelReady, setTemplatePanelReady] = useState(() => templateWizardSteps.includes(step));
  const groupRolesDropdownRef = useRef(null);
  const groupRolesPanelRef = useRef(null);
  const dropdownRef = useRef(null);
  const employeeDropdownPanelRef = useRef(null);
  const dateInputRef = useRef(null);

  const testNameInputRef = useRef(null);
  const groupNameInputRef = useRef(null);
  const templateBridgeRef = useRef(null);

  const writeDraftToStorage = useCallback((updated) => {
    try {
      localStorage.setItem('draft_assessment', JSON.stringify(createStorageSafeDraft(updated, 'default')));
    } catch (err) {
      if (err?.name === 'QuotaExceededError') {
        localStorage.setItem('draft_assessment', JSON.stringify(createStorageSafeDraft(updated, 'aggressive')));
        toast.error('Draft was too large, so preview questions were trimmed from browser storage.');
      } else {
        throw err;
      }
    }
    window.dispatchEvent(new Event('draft-updated'));
  }, []);

  const updateDraft = useCallback((fields) => {
    setDraft((prev) => {
      const updated = {
        ...prev,
        ...fields,
        updatedAt: new Date().toISOString(),
      };
      writeDraftToStorage(updated);
      return updated;
    });
  }, [writeDraftToStorage]);

  const handleWizardSelectQB = useCallback((qb) => {
    if (qb) {
      setSelectedQB(qb);
      setDraft((prev) => {
        const prevQs = prev.questionBank?.questions || [];
        const newQs = qb.questions || [];
        if (
          qb.id === prev.questionBank?.id &&
          qb.name === prev.questionBank?.name &&
          prevQs.length === newQs.length &&
          prevQs.every((q, i) => {
            const nq = newQs[i];
            return nq &&
              q.id === nq.id &&
              q.status === nq.status &&
              q.question === nq.question &&
              q.type === nq.type &&
              q.pedagogy === nq.pedagogy &&
              q.marks === nq.marks &&
              q.option_a === nq.option_a &&
              q.option_b === nq.option_b &&
              q.option_c === nq.option_c &&
              q.option_d === nq.option_d &&
              q.correct_answer === nq.correct_answer;
          })
        ) {
          return prev;
        }
        const updated = {
          ...prev,
          questionBank: {
            id: qb.id,
            name: qb.name,
            questions: qb.questions || [],
          },
          updatedAt: new Date().toISOString(),
        };
        writeDraftToStorage(updated);
        return updated;
      });
    } else {
      setSelectedQB(null);
      setDraft((prev) => {
        if (!prev.questionBank) return prev;
        const updated = { ...prev, questionBank: null, updatedAt: new Date().toISOString() };
        writeDraftToStorage(updated);
        return updated;
      });
    }
  }, [writeDraftToStorage]);

  const handleWizardSelectTemplate = useCallback((tpl) => {
    if (tpl) {
      setSelectedTemplate(tpl);
      setDraft((prev) => {
        const questionIds = (questions) => (questions || []).map((q) => String(q.id ?? '')).join('|');
        const isDifferent =
          !prev.template ||
          tpl.id !== prev.template.id ||
          tpl.name !== prev.template.name ||
          tpl.bank !== (prev.template.questionBank || '') ||
          Number(tpl.passMark) !== Number(prev.template.passMark || 0) ||
          JSON.stringify(tpl.sections) !== JSON.stringify(prev.template.sections || []) ||
          questionIds(tpl.questions) !== questionIds(prev.template.questions);

        if (!isDifferent) return prev;

        const updated = {
          ...prev,
          template: {
            id: tpl.id,
            name: tpl.name,
            questionBank: tpl.bank || '',
            passMark: (tpl.passMark !== undefined && tpl.passMark !== null && tpl.passMark !== '') ? Number(tpl.passMark) : 0,
            sections: tpl.sections || [],
            questions: tpl.questions || [],
          },
          updatedAt: new Date().toISOString(),
        };
        writeDraftToStorage(updated);
        return updated;
      });
    } else {
      setSelectedTemplate(null);
      setDraft((prev) => {
        if (!prev.template) return prev;
        const updated = { ...prev, template: null, updatedAt: new Date().toISOString() };
        writeDraftToStorage(updated);
        return updated;
      });
    }
  }, [writeDraftToStorage]);

  const savePendingGroupForm = useCallback((formState) => {
    updateDraft({ pendingGroupForm: normalizePendingGroupForm(formState) });
  }, [updateDraft]);

  const clearPendingGroupForm = useCallback(() => {
    setDraft((prev) => {
      if (!prev.pendingGroupForm) return prev;
      const { pendingGroupForm, ...rest } = prev;
      const updated = { ...rest, updatedAt: new Date().toISOString() };
      writeDraftToStorage(updated);
      return updated;
    });
  }, [writeDraftToStorage]);

  const handleHeaderPlusClick = useCallback((module) => {
    if (step === 'groups' && groupFormHasDraftContent(newGroup, showInputRow, editingGroupId)) {
      savePendingGroupForm({
        newGroup,
        editingGroupId,
        showInputRow,
        roleSearchTerm,
        candidateSearchTerm,
      });
    }
    if (navigateToModule) {
      navigateToModule(module, 'inventory');
    }
    localStorage.setItem('hr_upload_return_path', '/workflow/groups');
    localStorage.setItem('hr_upload_force_new', 'true');
    navigate('/assessment');
  }, [
    step,
    newGroup,
    showInputRow,
    editingGroupId,
    roleSearchTerm,
    candidateSearchTerm,
    savePendingGroupForm,
    navigateToModule,
    navigate,
  ]);

  useEffect(() => {
    if (step === 'create' && testNameInputRef.current) {
      testNameInputRef.current.focus();
    }
  }, [step]);

  useEffect(() => {
    if (step === 'groups' && showInputRow && !editingGroupId && groupNameInputRef.current) {
      groupNameInputRef.current.focus();
    }
  }, [step, showInputRow, editingGroupId]);

  // Load draft from localStorage once on mount (avoid re-hydrating on every step change)
  useEffect(() => {
    if (draftHydratedRef.current) return;

    const draftStr = localStorage.getItem('draft_assessment');
    if (!draftStr) {
      draftHydratedRef.current = true;
      return;
    }

    try {
      const parsed = JSON.parse(draftStr);
      if (parsed) {
        setDraft((prev) => ({ ...prev, ...parsed }));
        if (parsed.questionBank) {
          setSelectedQB(parsed.questionBank);
        }
        if (parsed.template) {
          setSelectedTemplate(parsed.template);
        }
        if (parsed.groups && parsed.groups.length > 0) {
          setShowInputRow(false);
        }
      }
    } catch (e) {
      console.error('Failed to parse draft_assessment', e);
    } finally {
      draftHydratedRef.current = true;
    }
  }, []);

  // Restore in-progress group form when returning to the groups step
  useEffect(() => {
    if (step !== 'groups') return;

    const draftStr = localStorage.getItem('draft_assessment');
    if (!draftStr) return;

    try {
      const parsed = JSON.parse(draftStr);
      if (parsed?.pendingGroupForm) {
        const pending = normalizePendingGroupForm(parsed.pendingGroupForm);
        isRestoringGroupFormRef.current = true;
        setNewGroup(pending.newGroup);
        setEditingGroupId(pending.editingGroupId);
        setShowInputRow(pending.showInputRow);
        setRoleSearchTerm(pending.roleSearchTerm);
        setCandidateSearchTerm(pending.candidateSearchTerm);
        requestAnimationFrame(() => {
          isRestoringGroupFormRef.current = false;
        });
      }
    } catch (e) {
      console.error('Failed to restore pending group form', e);
    }
  }, [step]);

  // Pre-mount template builder while on question bank so data is ready before the template step
  useEffect(() => {
    if (templateWizardSteps.includes(step)) {
      setTemplatePanelReady(true);
    }
  }, [step]);

  // Keep in-progress group form while navigating away (e.g. External Setup + icon)
  useEffect(() => {
    if (step !== 'groups' || isRestoringGroupFormRef.current) return;

    if (!groupFormHasDraftContent(newGroup, showInputRow, editingGroupId)) {
      return;
    }

    savePendingGroupForm({
      newGroup,
      editingGroupId,
      showInputRow,
      roleSearchTerm,
      candidateSearchTerm,
    });
  }, [
    step,
    newGroup,
    editingGroupId,
    showInputRow,
    roleSearchTerm,
    candidateSearchTerm,
    savePendingGroupForm,
  ]);

  // Reset inline group form state when category changes
  useEffect(() => {
    setNewGroup(getDefaultNewGroup(draft.template));
    setCandidatesList([]);
    setRoleSearchTerm('');
    setCandidateSearchTerm('');
    clearPendingGroupForm();
  }, [draft.category, clearPendingGroupForm, draft.template]);

  // Keep template_id in newGroup in sync with draft.template in real time without page refresh
  useEffect(() => {
    if (draft.template) {
      setNewGroup((prev) => {
        if (prev.template_id === draft.template.id) return prev;
        return {
          ...prev,
          template_id: draft.template.id,
          template_name: draft.template.name
        };
      });
    } else {
      setNewGroup((prev) => {
        if (!prev.template_id) return prev;
        return {
          ...prev,
          template_id: '',
          template_name: ''
        };
      });
    }
  }, [draft.template]);

  const getRolesForForm = () => {
    if (isHrCategory(draft.category)) {
      return collegeTemplates.map(t => ({
        id: t.id,
        name: t.name || t.template_name || t.id
      }));
    }
    return jobRoles.map(r => ({ ...r, name: r.name || r.role_name || r.job_role || r.role || r.id }));
  };

  const resolveRoleNames = (roleIds) => resolveAssessmentRoleNames(roleIds, getRolesForForm());

  const visibleGroupRoles = getRolesForForm().filter((role) => {
    const matchesSearch = (role.name || '').toLowerCase().includes(roleSearchTerm.toLowerCase());
    const isSelected = newGroup.user_role.map(String).includes(String(role.id));
    return matchesSearch && (!showSelectedRolesOnly || isSelected);
  });

  const toggleGroupRole = (roleId) => {
    const current = Array.isArray(newGroup.user_role)
      ? newGroup.user_role.map(String)
      : String(newGroup.user_role || '').split(',').map(s => s.trim()).filter(Boolean);
    const updated = current.includes(String(roleId))
      ? current.filter((id) => id !== String(roleId))
      : [...current, String(roleId)];
    setNewGroup({ ...newGroup, user_role: updated, candidates: [] });
  };

  const selectAllGroupRoles = (checked) => {
    if (checked) {
      const allIds = visibleGroupRoles.map((r) => String(r.id));
      setNewGroup({ ...newGroup, user_role: allIds, candidates: [] });
    } else {
      setNewGroup({ ...newGroup, user_role: [], candidates: [] });
    }
  };

  const getVisibleEmployees = () =>
    candidatesList.filter(
      (emp) =>
        (
          (emp.first_name || emp.name || '').toLowerCase().includes(candidateSearchTerm.toLowerCase()) ||
          (emp.email && emp.email.toLowerCase().includes(candidateSearchTerm.toLowerCase()))
        ) &&
        (!showSelectedCandidatesOnly || (Array.isArray(newGroup.candidates) && newGroup.candidates.map(String).includes(String(emp.id))))
    );

  const toggleEmployee = (empId) => {
    const current = Array.isArray(newGroup.candidates) ? newGroup.candidates.map(String) : [];
    const updated = current.includes(String(empId))
      ? current.filter(id => id !== String(empId))
      : [...current, String(empId)];
    setNewGroup({ ...newGroup, candidates: updated });
  };

  const isHrFresher = isHrCategory(draft.category);

  const isCollegeTemplateSelected = (template) => {
    const ids = (template.student_ids || []).map(String);
    const current = Array.isArray(newGroup.candidates) ? newGroup.candidates.map(String) : [];
    return ids.length > 0 && ids.every(id => current.includes(id));
  };

  const toggleCollegeTemplate = (template) => {
    const ids = (template.student_ids || []).map(String);
    const current = Array.isArray(newGroup.candidates) ? newGroup.candidates.map(String) : [];
    const allSelected = ids.length > 0 && ids.every(id => current.includes(id));
    const updated = allSelected
      ? current.filter(id => !ids.includes(id))
      : [...new Set([...current, ...ids])];
    setNewGroup({ ...newGroup, candidates: updated });
  };

  const getVisibleCollegeTemplates = () => collegeTemplates.filter(t =>
    (t.name || '').toLowerCase().includes(candidateSearchTerm.toLowerCase()) &&
    (!showSelectedCandidatesOnly || isCollegeTemplateSelected(t))
  );

  // Handle click outside for roles dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = groupRolesDropdownRef.current?.contains(event.target);
      const inPanel = groupRolesPanelRef.current?.contains(event.target);
      if (!inAnchor && !inPanel) {
        setShowRolesDropdown(false);
      }
    };
    if (showRolesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      setRoleSearchTerm('');
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRolesDropdown]);

  // Handle click outside for candidates dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = dropdownRef.current?.contains(event.target);
      const inPanel = employeeDropdownPanelRef.current?.contains(event.target);
      if (!inAnchor && !inPanel) {
        setShowCandidatesDropdown(false);
      }
    };
    if (showCandidatesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      setCandidateSearchTerm('');
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCandidatesDropdown]);

  // Handle click outside for date picker to force blur
  useEffect(() => {
    const handleClickOutsideDate = (event) => {
      if (dateInputRef.current && !dateInputRef.current.contains(event.target)) {
        setTimeout(() => {
          if (dateInputRef.current) {
            dateInputRef.current.blur();
          }
        }, 100);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideDate);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideDate);
    };
  }, []);

  // Validation redirect interceptor
  useEffect(() => {
    const draftStr = localStorage.getItem('draft_assessment');
    let currentDraft = draft;
    if (draftStr) {
      try {
        currentDraft = JSON.parse(draftStr);
      } catch (e) {}
    }

    const earliestStep = getEarliestIncompleteStep(currentDraft);
    const stepOrder = ['create', 'question-bank', 'template', 'groups', 'publish'];
    const currentStepIndex = stepOrder.indexOf(step);
    const earliestStepIndex = stepOrder.indexOf(earliestStep);

    if (currentStepIndex > earliestStepIndex) {
      toast.error(`Please complete the ${stepOrder[earliestStepIndex].replace('-', ' ')} step first.`);
      navigate(`/workflow/${earliestStep}`);
    }
  }, [step, navigate]);

  // Fetch Step 2: Question Banks
  useEffect(() => {
    if (step === 'question-bank' || step === 'groups' || step === 'publish') {
      setLoadingQB(true);
      testService.getQuestionBanks()
        .then(res => {
          if (res.status === 'success' && res.banks) {
            setQuestionBanks(res.banks);
            const draftStr = localStorage.getItem('draft_assessment');
            const parsed = draftStr ? JSON.parse(draftStr) : null;
            if (parsed?.questionBank?.id || parsed?.questionBank?.name) {
              const matched = res.banks.find(b => 
                (parsed.questionBank.id && String(b.id) === String(parsed.questionBank.id)) ||
                (parsed.questionBank.name && b.name === parsed.questionBank.name)
              );
              if (matched) {
                const mergedQB = {
                  ...matched,
                  questions: parsed.questionBank.questions || matched.questions || []
                };
                setSelectedQB(mergedQB);
                if (!parsed.questionBank.id || !parsed.questionBank.name) {
                  updateDraft({
                    questionBank: {
                      ...parsed.questionBank,
                      id: matched.id,
                      name: matched.name
                    }
                  });
                }
              }
            }
          }
        })
        .catch(err => {
          console.error(err);
          toast.error('Failed to load question banks');
        })
        .finally(() => setLoadingQB(false));
    }
  }, [step]);

  // Fetch Step 3: Templates (prefetch on question-bank; TemplateBuilder owns wizard template UI state)
  useEffect(() => {
    if (!templateWizardSteps.includes(step)) return;
    if (templatesLoadedRef.current && templates.length > 0) return;

    setLoadingTemplates(true);
    testService.getTemplates()
      .then(res => {
        if (res.status === 'success' && res.templates) {
          setTemplates(res.templates);
          templatesLoadedRef.current = true;

          // On template step, TemplateBuilder hydrates itself from draftTemplate prop.
          // However, if the draft has a template id but no sections (incomplete earlier sync),
          // update the draft with full data so TemplateBuilder gets the complete template.
          if (step === 'template') {
            const draftStr = localStorage.getItem('draft_assessment');
            const parsed = draftStr ? JSON.parse(draftStr) : null;
            const hasSections = Array.isArray(parsed?.template?.sections) && parsed.template.sections.length > 0;
            if (parsed?.template?.id && !hasSections) {
              const matched = res.templates.find(t => String(t.id) === String(parsed.template.id));
              if (matched) {
                const needsFullFetch = (matched.question_count ?? matched.total_questions ?? 0) > 0 &&
                  (!Array.isArray(matched.sections) || matched.sections.length === 0);
                if (needsFullFetch) {
                  testService.getTemplate(matched.id)
                    .then(fullRes => {
                      if (fullRes?.status === 'success' && fullRes.template) {
                        updateDraft({ template: { ...parsed.template, ...fullRes.template } });
                      }
                    })
                    .catch(() => {});
                } else if (Array.isArray(matched.sections) && matched.sections.length > 0) {
                  updateDraft({ template: { ...parsed.template, ...matched } });
                }
              }
            }
            return;
          }

            const draftStr = localStorage.getItem('draft_assessment');
            const parsed = draftStr ? JSON.parse(draftStr) : null;
            if (parsed?.template?.id || parsed?.template?.name) {
              const matched = res.templates.find(t => 
                (parsed.template.id && String(t.id) === String(parsed.template.id)) ||
                (parsed.template.name && t.name === parsed.template.name)
              );
              if (matched) {
                const applyMatchedTemplate = (tpl) => {
                  setSelectedTemplate(tpl);
                  const mergedTemplate = buildDraftTemplateFromApi(tpl, parsed?.template);
                  const shouldSyncTemplate =
                    step === 'publish' ||
                    !parsed?.template?.id ||
                    !parsed?.template?.name ||
                    !(parsed?.template?.sections?.length) ||
                    getTemplatePassMark(parsed?.template) === 0 ||
                    getTemplateTotalMarks(parsed?.template) === 0;

                  if (shouldSyncTemplate) {
                    updateDraft({ template: mergedTemplate });
                  }
                };

                const needsFullTemplate =
                  (matched.question_count ?? matched.total_questions ?? 0) > 0 &&
                  (!Array.isArray(matched.sections) || matched.sections.length === 0);

                if (needsFullTemplate) {
                  testService.getTemplate(matched.id)
                    .then((fullRes) => {
                      if (fullRes?.status === 'success' && fullRes.template) {
                        applyMatchedTemplate(fullRes.template);
                      } else {
                        applyMatchedTemplate(matched);
                      }
                    })
                    .catch(() => applyMatchedTemplate(matched));
                } else {
                  applyMatchedTemplate(matched);
                }
              }
            }
        }
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load templates');
      })
      .finally(() => setLoadingTemplates(false));
  }, [step, updateDraft, templates.length]);

  const wizardDraftTemplate = useMemo(() => {
    if (!draft.template) return null;
    return draft.template;
  }, [
    draft.template?.id,
    draft.template?.name,
    draft.template?.questionBank,
    draft.template?.passMark,
    JSON.stringify(draft.template?.sections || []),
  ]);

  const wizardDraftQuestionBank = useMemo(() => {
    if (!draft.questionBank) return null;
    return draft.questionBank;
  }, [
    draft.questionBank?.id,
    draft.questionBank?.name,
    JSON.stringify(draft.questionBank?.questions || [])
  ]);

  // Fetch Step 4 / Review roles & colleges
  useEffect(() => {
    if (step === 'groups' || step === 'publish') {
      testService.getJobRoles()
        .then(res => {
          if (res.status === 'success') {
            setJobRoles(res.data || []);
          } else if (Array.isArray(res)) {
            setJobRoles(res);
          }
        })
        .catch(e => console.error('Error fetching job roles', e));

      testService.getHrCollegeTemplates()
        .then(res => {
          if (res.status === 'success' && res.college_templates) {
            const mapped = (res.college_templates || []).map((item) => ({
              id: item.template_name || item.name || '',
              name: item.template_name || item.name || '',
              student_count: parseInt(item.student_count || item.total_students) || 0,
              student_ids: Array.isArray(item.student_ids)
                ? item.student_ids.map(String)
                : String(item.student_ids || '').split(',').map(s => s.trim()).filter(Boolean)
            }));
            setCollegeTemplates(mapped);
          }
        })
        .catch(e => console.error('Error fetching college templates', e));
    }
  }, [step]);

  // Fetch candidates list when roles change in Add Group modal
  useEffect(() => {
    if (step === 'groups' && newGroup.user_role.length > 0) {
      setLoadingCandidates(true);
      if (!isHrCategory(draft.category)) {
        testService.getUsersByRoles(newGroup.user_role.join(','))
          .then(res => {
            const list = res.data || (Array.isArray(res) ? res : []);
            const mapped = list.map(emp => ({
              id: emp.id,
              first_name: emp.first_name || emp.name || '',
              last_name: emp.last_name || '',
              email: emp.email || emp.username || '',
            }));
            setCandidatesList(mapped);
          })
          .catch(e => console.error(e))
          .finally(() => setLoadingCandidates(false));
      } else {
        // External Category: Fetch candidates/students for all selected roles (colleges)
        Promise.all(
          newGroup.user_role.map(roleName => 
            testService.getStudentsByCollege(roleName)
              .then(res => res.students || [])
              .catch(() => [])
          )
        ).then(results => {
          const allStudents = results.flat();
          const uniqueStudents = [];
          const seenIds = new Set();
          allStudents.forEach(s => {
            const uniqueId = String(s.id || s.username || s.email || '');
            if (uniqueId && !seenIds.has(uniqueId)) {
              seenIds.add(uniqueId);
              uniqueStudents.push({
                id: s.id,
                first_name: s.student_name || s.name || '',
                last_name: '',
                email: s.email || s.username || '',
              });
            }
          });
          setCandidatesList(uniqueStudents);
        })
        .catch(e => console.error(e))
        .finally(() => setLoadingCandidates(false));
      }
    } else {
      setCandidatesList([]);
    }
  }, [newGroup.user_role, draft.category, step]);

  // Synchronize candidatesList into resolvedCandidatesMap to avoid re-fetching
  useEffect(() => {
    if (candidatesList.length > 0) {
      setResolvedCandidatesMap(prev => {
        const updated = { ...prev };
        candidatesList.forEach(c => {
          updated[String(c.id)] = c;
        });
        return updated;
      });
    }
  }, [candidatesList]);

  // Auto-resolve candidate details for all saved groups in the draft
  useEffect(() => {
    if (step !== 'groups' || !draft.groups || draft.groups.length === 0) return;

    draft.groups.forEach(g => {
      const candidateIds = String(g.candidates || '').split(',').filter(Boolean);
      if (candidateIds.length === 0) return;

      const hasUnresolved = candidateIds.some(cid => !resolvedCandidatesMap[String(cid)]);
      if (!hasUnresolved) return;

      const rolesArray = Array.isArray(g.user_role)
        ? g.user_role
        : String(g.user_role || '').split(',').map(s => s.trim()).filter(Boolean);

      if (!isHrCategory(draft.category)) {
        testService.getUsersByRoles(rolesArray.join(','))
          .then(res => {
            const list = res.data || (Array.isArray(res) ? res : []);
            setResolvedCandidatesMap(prev => {
              const updated = { ...prev };
              list.forEach(emp => {
                updated[String(emp.id)] = {
                  id: emp.id,
                  first_name: emp.first_name || emp.name || '',
                  last_name: emp.last_name || '',
                  email: emp.email || emp.username || '',
                };
              });
              return updated;
            });
          })
          .catch(e => console.error('Error auto-fetching group users:', e));
      } else {
        // External Category
        Promise.all(
          rolesArray.map(roleName => 
            testService.getStudentsByCollege(roleName)
              .then(res => res.students || [])
              .catch(() => [])
          )
        ).then(results => {
          const allStudents = results.flat();
          setResolvedCandidatesMap(prev => {
            const updated = { ...prev };
            allStudents.forEach(s => {
              updated[String(s.id)] = {
                id: s.id,
                first_name: s.student_name || s.name || '',
                last_name: '',
                email: s.email || s.username || '',
              };
            });
            return updated;
          });
        })
        .catch(e => console.error('Error auto-fetching group students:', e));
      }
    });
  }, [step, draft.groups, draft.category, resolvedCandidatesMap]);

  // Stepper Header click handler
  const handleStepClick = (targetStep) => {
    if (isSubmitting) return;
    const stepOrder = ['create', 'question-bank', 'template', 'groups', 'publish'];
    const currentIndex = stepOrder.indexOf(step);
    const targetIndex = stepOrder.indexOf(targetStep.id);

    if (step === 'groups' && (editingGroupId || (showInputRow && newGroup.pack_name.trim() !== ''))) {
      if (targetIndex > currentIndex) {
        toast.error('Please save or cancel the group you are editing first.');
        return;
      } else {
        savePendingGroupForm({
          newGroup,
          editingGroupId,
          showInputRow,
          roleSearchTerm,
          candidateSearchTerm,
        });
      }
    }

    const earliestStep = getEarliestIncompleteStep(draft);
    const earliestIndex = stepOrder.indexOf(earliestStep);

    // Can only click back to completed steps or the earliest incomplete step
    if (targetIndex <= earliestIndex || (draft.completedSteps || []).includes(targetStep.id)) {
      navigate(targetStep.path);
    } else {
      toast.error('Please click the Save button to save the current step first.');
    }
  };

  const syncTestAndVideosToBackend = async (currentDraft) => {
    const serverVideoUrls = (currentDraft.videoFiles || [])
      .map(f => typeof f === 'string' ? f : (f && typeof f === 'object' && f.url ? f.url : ''))
      .filter(u => u && (u.startsWith('/') || u.includes('/')));

    const payload = {
      name:               currentDraft.name || 'Untitled Draft',
      category:           currentDraft.category || 'Enova',
      code:               currentDraft.id || `TST-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
      assessment_type:    currentDraft.type || null,
      assigned_to:        null,
      description:        (currentDraft.instructions || '').substring(0, 500),
      instructions:       currentDraft.instructions || '',
      proctored_exam:     currentDraft.fullscreenMode    ? 1 : 0,
      browser_lockdown:   currentDraft.tabSwitchWarning  ? 1 : 0,
      show_results:       currentDraft.autoSubmit        ? 1 : 0,
      allow_backtracking: currentDraft.navigationControl ? 1 : 0,
      add_video:          currentDraft.videoEnabled      ? 1 : 0,
      intro_videos:       serverVideoUrls,
      template_id:        currentDraft.template?.id || null,
      question_bank:      currentDraft.questionBank?.name || null,
      status:             currentDraft.status || 'Draft',
    };

    let backendId = currentDraft.backendDraftId || currentDraft.backendId || null;

    if (backendId) {
      // Already in DB — update the existing record
      await testService.updateTest(backendId, payload);
    } else if (currentDraft.name && currentDraft.name.trim()) {
      // First save — create a new Draft record in DB
      const res = await testService.createTest(payload);
      if (res && res.status === 'success' && res.id) {
        backendId = res.id;
      } else {
        throw new Error(res?.message || 'Failed to create assessment on backend');
      }
    }

    if (!backendId) {
      return { backendId: null, updatedVideoFiles: currentDraft.videoFiles };
    }

    // Upload intro videos if there are any new local files
    let updatedVideoFiles = currentDraft.videoFiles || [];
    const filesToUpload = [];
    for (const fileObj of updatedVideoFiles) {
      const name = fileObj && typeof fileObj === 'object' ? fileObj.name : fileObj;
      const cachedFile = window.selectedVideoFiles?.[name];
      if (cachedFile) {
        filesToUpload.push(cachedFile);
      }
    }

    if (filesToUpload.length > 0) {
      const uploadRes = await testService.uploadIntroVideos(backendId, filesToUpload);
      if (uploadRes && uploadRes.status === 'success') {
        // Clear cached files from window
        for (const fileObj of updatedVideoFiles) {
          const name = fileObj && typeof fileObj === 'object' ? fileObj.name : fileObj;
          if (window.selectedVideoFiles?.[name]) {
            delete window.selectedVideoFiles[name];
          }
        }
        updatedVideoFiles = uploadRes.intro_videos || uploadRes.data?.intro_videos || [];
      } else {
        throw new Error(uploadRes?.message || 'Failed to upload intro videos to backend');
      }
    }

    return { backendId, updatedVideoFiles };
  };

  const handleSaveAndExit = async () => {
    if (isSubmitting) return;
    if (step === 'groups' && (editingGroupId || (showInputRow && newGroup.pack_name.trim() !== ''))) {
      toast.error('Please save or cancel the group you are editing first.');
      return;
    }

    setIsSubmitting(true);
    const savingToast = toast.loading('Saving draft test...');
    try {
      const currentDraft = { ...draft, currentStep: step };
      
      let syncedGroups = draft.groups || [];
      const backendId = currentDraft.backendDraftId || currentDraft.backendId;
      if (backendId && (step === 'groups' || step === 'publish') && draft.groups && draft.groups.length > 0) {
        syncedGroups = await syncDraftGroupsToBackend(backendId, draft.groups);
      }
      currentDraft.groups = syncedGroups;

      const { backendId: syncedBackendId, updatedVideoFiles } = await syncTestAndVideosToBackend(currentDraft);
      
      const creationDate = currentDraft.createdAt || currentDraft.created_at || new Date().toISOString();
      const draftToSave = { 
        ...currentDraft, 
        backendDraftId: syncedBackendId || backendId || currentDraft.backendDraftId,
        videoFiles: updatedVideoFiles,
        createdAt: creationDate,
        created_at: creationDate
      };
      
      updateDraft(draftToSave);

      const savedDraftsStr = localStorage.getItem('saved_drafts') || '[]';
      try {
        let savedDrafts = JSON.parse(savedDraftsStr);
        savedDrafts = savedDrafts.filter(d => d.id !== currentDraft.id);
        savedDrafts.unshift(draftToSave);
        localStorage.setItem('saved_drafts', JSON.stringify(savedDrafts));
      } catch (e) {
        console.error(e);
      }

      toast.dismiss(savingToast);
      toast.success('Draft test saved successfully!');
    } catch (err) {
      toast.dismiss(savingToast);
      console.error('Failed to persist draft to backend:', err);
      // Draft is still safe in localStorage
      toast.success('Draft saved locally. (Server sync failed)');
    } finally {
      setIsSubmitting(false);
    }

    localStorage.setItem('assessment_active_module', 'inventory');
    navigate('/assessment');
  };


  const commitPendingGroupFromForm = () => {
    const hasPendingEdit = editingGroupId || (showInputRow && newGroup.pack_name.trim() !== '');
    if (!hasPendingEdit) {
      return { ok: true, groups: draft.groups || [], committed: false };
    }

    if (!newGroup.pack_name.trim()) return { ok: false, error: 'Group name is required' };
    if (!newGroup.scheduled_date) return { ok: false, error: 'Date is required' };
    if (!newGroup.start_time || !newGroup.end_time) return { ok: false, error: 'Start and End times are required' };

    if (isHrFresher && newGroup.user_role.length === 0) {
      return { ok: false, error: 'Please select a college name' };
    }
    if (!isHrFresher && newGroup.user_role.length === 0) {
      return { ok: false, error: 'Please select at least one role' };
    }
    if (isHrFresher && newGroup.candidates.length === 0) {
      return { ok: false, error: 'The selected college contains no candidates' };
    }
    if (!isHrFresher && newGroup.candidates.length === 0) {
      return { ok: false, error: 'Please select candidates' };
    }

    const resolvedUserRole = newGroup.user_role.join(',');
    const groupPayload = {
      pack_name: newGroup.pack_name.trim(),
      template_id: newGroup.template_id || draft.template?.id || '',
      template_name: newGroup.template_name || draft.template?.name || 'Default Template',
      user_role: resolvedUserRole,
      candidates: newGroup.candidates.join(','),
      scheduled_date: newGroup.scheduled_date,
      start_time: newGroup.start_time,
      end_time: newGroup.end_time,
      duration: newGroup.duration
    };

    if (editingGroupId) {
      const updatedGroups = (draft.groups || []).map(g => (
        g.id === editingGroupId ? { ...g, ...groupPayload } : g
      ));
      return { ok: true, groups: updatedGroups, committed: true, mode: 'update' };
    }

    const groupObject = {
      id: 'group-' + Date.now(),
      ...groupPayload,
      status: 'Draft'
    };
    return {
      ok: true,
      groups: [...(draft.groups || []), groupObject],
      committed: true,
      mode: 'create'
    };
  };

  const resetGroupForm = () => {
    setEditingGroupId(null);
    setNewGroup(getDefaultNewGroup(draft.template));
    setShowInputRow(false);
    setRoleSearchTerm('');
    setCandidateSearchTerm('');
    clearPendingGroupForm();
  };

  const handleWizardBack = () => {
    const stepOrder = ['create', 'question-bank', 'template', 'groups', 'publish'];
    const currentIndex = stepOrder.indexOf(step);
    if (currentIndex > 0) {
      const prevStep = stepOrder[currentIndex - 1];
      if (step === 'groups' && (editingGroupId || (showInputRow && newGroup.pack_name.trim() !== ''))) {
        savePendingGroupForm({
          newGroup,
          editingGroupId,
          showInputRow,
          roleSearchTerm,
          candidateSearchTerm,
        });
      }
      updateDraft({ currentStep: prevStep });
      navigate('/workflow/' + prevStep);
    } else {
      localStorage.setItem('assessment_active_module', 'inventory');
      navigate('/assessment');
    }
  };

  const syncDraftGroupsToBackend = async (assessmentId, groupsList) => {
    if (!assessmentId) return [];

    // 1. Delete any deleted groups
    if (deletedPackIds.length > 0) {
      for (const id of deletedPackIds) {
        try {
          await testService.deleteTestPack(id);
        } catch (e) {
          console.error(`Failed to delete pack ${id}:`, e);
        }
      }
      setDeletedPackIds([]);
    }

    // 2. Save / update remaining groups
    const updatedGroups = [];
    for (const g of groupsList) {
      const payload = {
        assessment_id: assessmentId,
        pack_name: g.pack_name,
        template_id: g.template_id || draft.template?.id || null,
        user_role: Array.isArray(g.user_role)
          ? g.user_role.join(',')
          : (g.user_role ? String(g.user_role) : ''),
        candidates: Array.isArray(g.candidates)
          ? g.candidates.filter(Boolean).join(',')
          : (typeof g.candidates === 'string' ? g.candidates.trim() : ''),
        scheduled_date: g.scheduled_date,
        start_time: g.start_time,
        end_time: g.end_time,
        duration: g.duration || 60,
        status: g.status || 'Draft',
      };

      const packId = g.id || g.backendPackId;
      const isExisting = packId && !String(packId).startsWith('group-');
      try {
        if (isExisting) {
          const res = await testService.updateTestPack(packId, payload);
          if (res?.status === 'success') {
            updatedGroups.push({ ...g, backendPackId: packId, id: packId });
          } else {
            updatedGroups.push(g);
          }
        } else {
          const res = await testService.saveTestPack(payload);
          if (res?.status === 'success' && res.id) {
            updatedGroups.push({ ...g, backendPackId: res.id, id: res.id });
          } else {
            updatedGroups.push(g);
          }
        }
      } catch (err) {
        console.error('Failed to sync group to backend:', err);
        updatedGroups.push(g);
      }
    }
    return updatedGroups;
  };

  const handleSaveAndContinue = async () => {
    if (isSubmitting) return;

    // Validate current step
    if (step === 'create') {
      if (!draft.name.trim()) return toast.error('Test name is required');
      if (!draft.type) return toast.error('Test type is required');
      if (!draft.instructions.trim()) return toast.error('Instructions are required');
      if (draft.videoEnabled && (!draft.videoFiles || draft.videoFiles.length === 0)) return toast.error('Please upload at least one video file');

      setIsSubmitting(true);
      const savingToast = toast.loading('Saving test details...');
      try {
        const currentDraft = { ...draft };
        const { backendId, updatedVideoFiles } = await syncTestAndVideosToBackend(currentDraft);
        
        const completed = [...new Set([...draft.completedSteps, 'create'])];
        updateDraft({
          backendDraftId: backendId,
          videoFiles: updatedVideoFiles,
          completedSteps: completed,
          currentStep: 'question-bank'
        });
        
        toast.dismiss(savingToast);
        toast.success('Test details saved successfully!');
        navigate('/workflow/question-bank');
      } catch (err) {
        toast.dismiss(savingToast);
        console.error('Failed to save test details:', err);
        toast.error(err.message || 'Failed to save test details');
      } finally {
        setIsSubmitting(false);
      }
    } 
    else if (step === 'question-bank') {
      if (!selectedQB) return toast.error('Please select a question bank');
      if (!selectedQB.name.trim()) return toast.error('Please provide a name for the Question Bank');
      
      const isDuplicate = questionBanks.some(b => 
        b.name.trim().toLowerCase() === selectedQB.name.trim().toLowerCase() && 
        String(b.id) !== String(selectedQB.id)
      );
      if (isDuplicate) {
        return toast.error("A question bank with this name already exists. Please choose a unique name.");
      }

      if (!selectedQB.questions || selectedQB.questions.length === 0) {
        return toast.error('Please add at least one question to the bank');
      }

      setIsSubmitting(true);
      const savingToast = toast.loading('Saving Question Bank...');
      try {
        let repoId = selectedQB.id;
        if (!repoId) {
          const bankRes = await testService.saveQuestionBank({ name: selectedQB.name });
          if (bankRes.status === 'success') {
            repoId = bankRes.id;
          } else {
            throw new Error(bankRes.message || "Failed to create Question Bank");
          }
        }

        const bulkRes = await testService.bulkSaveQBQuestions({
          repository_id: repoId,
          questions: selectedQB.questions
        });

        if (bulkRes.status !== 'success') {
          throw new Error(bulkRes.message || "Failed to save Questions");
        }

        const updatedQB = {
          ...selectedQB,
          id: repoId
        };
        setSelectedQB(updatedQB);
        
        const completed = [...new Set([...draft.completedSteps, 'question-bank'])];
        const nextDraft = {
          ...draft,
          questionBank: {
            id: updatedQB.id,
            name: updatedQB.name,
            questions: updatedQB.questions || []
          },
          completedSteps: completed,
          currentStep: 'template'
        };

        await syncTestAndVideosToBackend(nextDraft);

        toast.dismiss(savingToast);
        toast.success('Question Bank saved successfully!');

        updateDraft(nextDraft);
        navigate('/workflow/template');
      } catch (err) {
        toast.dismiss(savingToast);
        toast.error(err.message || 'Failed to save Question Bank');
        console.error(err);
      } finally {
        setIsSubmitting(false);
      }
    }
    else if (step === 'template') {
      templateBridgeRef.current?.flushSync?.();
      const editorTemplate = templateBridgeRef.current?.getSnapshot?.() || null;
      const templateToSave = editorTemplate?.name?.trim() ? editorTemplate : selectedTemplate;

      if (!templateToSave) return toast.error('Please select or create a template');
      if (!templateToSave.name?.trim()) return toast.error('Please enter a Template Name');
      
      const isDuplicate = templates.some(t => 
        t.name.trim().toLowerCase() === templateToSave.name.trim().toLowerCase() && 
        String(t.id) !== String(templateToSave.id)
      );
      if (isDuplicate) {
        return toast.error("A template with this name already exists. Please choose a unique name.");
      }

      if (!templateToSave.sections || templateToSave.sections.length === 0) {
        return toast.error('Please add at least one section');
      }

      if (!templateToSave.questions || templateToSave.questions.length === 0) {
        return toast.error('Please add or generate at least one question in the template');
      }

      setIsSubmitting(true);
      const savingToast = toast.loading('Saving Template...');
      try {
        let finalId = templateToSave.id;
        let finalName = templateToSave.name;
        
        // Anti-Mutation Protection: Check if this is an old DB template being modified
        const oldTemplate = templates.find(t => String(t.id) === String(templateToSave.id));
        if (oldTemplate) {
          const nameChanged = oldTemplate.name.trim() !== templateToSave.name.trim();
          const marksChanged = Number(oldTemplate.pass_mark ?? oldTemplate.passMark ?? 0) !== Number(templateToSave.passMark ?? 0);
          const questionsChanged = Number(oldTemplate.total_questions ?? oldTemplate.question_count ?? oldTemplate.questions?.length ?? 0) !== (templateToSave.questions?.length ?? 0);
          const bankChanged = String(oldTemplate.bank ?? '') !== String(templateToSave.bank ?? templateToSave.questionBank ?? '');
          
          const isModified = nameChanged || marksChanged || questionsChanged || bankChanged;
          
          if (isModified) {
            finalId = null; // Strip ID to force a new clone
            if (!nameChanged) {
               let copyName = `${templateToSave.name} (Copy)`;
               let counter = 1;
               while (templates.some(t => t.name.trim().toLowerCase() === copyName.trim().toLowerCase())) {
                 counter++;
                 copyName = `${templateToSave.name} (Copy ${counter})`;
               }
               finalName = copyName;
            }
          }
        }

        const isDbId = (val) => {
          if (!val) return false;
          const num = Number(val);
          return !isNaN(num) && Number.isInteger(num) && num > 0 && num < 10000000000;
        };

        const payload = {
          id: isDbId(finalId) ? Number(finalId) : null,
          name: finalName,
          passMark: (templateToSave.passMark !== undefined && templateToSave.passMark !== null && templateToSave.passMark !== '') ? Math.round(Number(templateToSave.passMark)) : 0,
          totalQuestions: Math.round(templateToSave.questions?.length || 0),
          totalMarks: Math.round(templateToSave.questions?.reduce((sum, q) => sum + Number(q.marks || 0), 0) || 0),
          bank: templateToSave.bank || templateToSave.questionBank || '',
          sections: (templateToSave.sections || []).map(s => ({
            name: s.name || s.section_name || 'Section',
            type: s.type || s.marks_type || 'MCQ',
            count: Math.round(Number(s.count || s.num_questions || 0)),
            marks: Math.round(Number(s.marks || s.marks_per_question || 1))
          })),
          questions: (templateToSave.questions || []).map(q => ({
            ...q,
            marks: Math.round(Number(q.marks || 1))
          }))
        };
        const res = await testService.saveTemplate(payload);
        if (res.status === 'success') {
          const updatedTemplate = {
            ...templateToSave,
            id: res.id,
            bank: payload.bank,
            questionBank: payload.bank,
            questions: payload.questions,
            sections: payload.sections,
          };
          setSelectedTemplate(updatedTemplate);
          handleWizardSelectTemplate(updatedTemplate);
          setTemplates((prev) => {
            const exists = prev.some(t => String(t.id) === String(res.id));
            if (exists) {
              return prev.map(t => String(t.id) === String(res.id) ? { ...t, name: updatedTemplate.name } : t);
            } else {
              return [...prev, { id: res.id, name: updatedTemplate.name }];
            }
          });

          const completed = [...new Set([...draft.completedSteps, 'template'])];
          const nextDraft = {
            ...draft,
            template: {
              id: updatedTemplate.id,
              name: updatedTemplate.name,
              questionBank: updatedTemplate.bank || updatedTemplate.questionBank || '',
              passMark: (updatedTemplate.passMark !== undefined && updatedTemplate.passMark !== null && updatedTemplate.passMark !== '') ? Number(updatedTemplate.passMark) : 0,
              sections: updatedTemplate.sections || [],
              questions: updatedTemplate.questions || [],
            },
            completedSteps: completed,
            currentStep: 'groups'
          };

          await syncTestAndVideosToBackend(nextDraft);

          toast.dismiss(savingToast);
          toast.success(templateToSave.id ? 'Template updated successfully!' : 'Template saved successfully!');

          updateDraft(nextDraft);
          navigate('/workflow/groups');
        } else {
          throw new Error(res.message || "Failed to save template");
        }
      } catch (err) {
        toast.dismiss(savingToast);
        const errMsg = err.response?.data?.message || err.message || 'Failed to save Template';
        toast.error(errMsg);
        console.error(err);
      } finally {
        setIsSubmitting(false);
      }
    }
    else if (step === 'groups') {
      const result = commitPendingGroupFromForm();
      if (!result.ok) {
        if (result.error) toast.error(result.error);
        return;
      }
      if (!result.groups || result.groups.length === 0) return toast.error('Please assign at least one group');

      if (result.committed) {
        resetGroupForm();
      }

      setIsSubmitting(true);
      const savingToast = toast.loading('Saving groups...');
      try {
        const backendId = draft.backendDraftId || draft.backendId;
        const syncedGroups = await syncDraftGroupsToBackend(backendId, result.groups);

        const completed = [...new Set([...(draft.completedSteps || []), 'groups'])];
        const nextDraft = {
          ...draft,
          groups: syncedGroups,
          completedSteps: completed,
          currentStep: 'publish'
        };

        await syncTestAndVideosToBackend(nextDraft);
        toast.dismiss(savingToast);
        toast.success('Groups saved successfully!');
        updateDraft(nextDraft);
        navigate('/workflow/publish');
      } catch (err) {
        toast.dismiss(savingToast);
        toast.error(err.message || 'Failed to save groups');
        console.error(err);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Question bank selection handler
  const handleSelectQB = (qb) => {
    setSelectedQB(qb);
  };

  // Template selection handler
  const handleSelectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
  };

  // Group creation handlers
  const handleCalculateDuration = (start, end) => {
    if (!start || !end) return 0;
    const [sH, sM] = start.split(':').map(Number);
    const [eH, eM] = end.split(':').map(Number);
    let diff = (eH * 60 + eM) - (sH * 60 + sM);
    if (diff < 0) diff += 1440;
    return diff;
  };

  const handleTimeFieldChange = (field, val) => {
    const updated = { ...newGroup, [field]: val };
    if (field === 'start_time') {
      updated.end_time = val;
    }
    const dur = handleCalculateDuration(updated.start_time, updated.end_time);
    setNewGroup({ ...updated, duration: dur });
  };

  const handleAddGroupSubmit = (e) => {
    if (e) e.preventDefault();
    const result = commitPendingGroupFromForm();
    if (!result.ok) {
      if (result.error) toast.error(result.error);
      return;
    }
    if (!result.committed) return;

    updateDraft({ groups: result.groups });
    resetGroupForm();
    toast.success(result.mode === 'update' ? 'Group updated successfully' : 'Group added successfully');
  };

  const handleDeleteGroup = (gId) => {
    const targetGroup = draft.groups.find(g => g.id === gId);
    const backendId = targetGroup?.backendPackId || targetGroup?.backendId || 
                      (typeof gId === 'number' || (!isNaN(Number(gId)) && String(gId).trim() !== '') ? Number(gId) : null);
    if (backendId) {
      setDeletedPackIds(prev => [...prev, backendId]);
    }
    const updated = draft.groups.filter(g => g.id !== gId);
    updateDraft({ groups: updated });
    if (editingGroupId === gId) {
      setEditingGroupId(null);
      setNewGroup(getDefaultNewGroup(draft.template));
    }
    toast.success('Group removed.');
  };

  const handleEditGroup = (g) => {
    if (editingGroupId || (showInputRow && newGroup.pack_name.trim() !== '')) {
      toast.error('Please save or cancel your current group edit first.');
      return;
    }
    setEditingGroupId(g.id);
    setNewGroup({
      pack_name: g.pack_name || '',
      template_id: g.template_id || '',
      template_name: g.template_name || '',
      user_role: Array.isArray(g.user_role)
        ? g.user_role.map(String)
        : String(g.user_role || '').split(',').map(s => s.trim()).filter(Boolean),
      candidates: Array.isArray(g.candidates)
        ? g.candidates.map(String)
        : String(g.candidates || '').split(',').map(s => s.trim()).filter(Boolean),
      scheduled_date: g.scheduled_date || '',
      start_time: g.start_time || '',
      end_time: g.end_time || '',
      duration: g.duration || 0
    });
    setShowInputRow(true);
  };

  const handleQuestionDoubleClick = (q) => {
    setViewingQuestion(q);
  };

  // Save handler
  const handlePublishAssessment = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    const publishingToast = toast.loading('Publishing test and assigning groups...');
    try {
      // 1. Sync the test with status: 'Published'
      const { backendId, updatedVideoFiles } = await syncTestAndVideosToBackend({
        ...draft,
        status: 'Published'
      });

      // Delete any deleted groups first if there are any pending deletions
      if (deletedPackIds.length > 0) {
        for (const dId of deletedPackIds) {
          try {
            await testService.deleteTestPack(dId);
          } catch (e) {
            console.error('Failed to clean up deleted pack:', e);
          }
        }
        setDeletedPackIds([]);
      }

      // 2. Sync and publish each group
      const draftGroups = [];
      if (backendId && draft.groups && draft.groups.length > 0) {
        for (const g of draft.groups) {
          try {
            const payload = {
              assessment_id: backendId,
              pack_name: g.pack_name,
              template_id: g.template_id || draft.template?.id || null,
              user_role: Array.isArray(g.user_role)
                ? g.user_role.join(',')
                : (g.user_role ? String(g.user_role) : ''),
              candidates: Array.isArray(g.candidates)
                ? g.candidates.filter(Boolean).join(',')
                : (typeof g.candidates === 'string' ? g.candidates.trim() : ''),
              scheduled_date: g.scheduled_date,
              start_time: g.start_time,
              end_time: g.end_time,
              duration: g.duration || 60,
              status: 'Draft',
            };

            const packId = g.id || g.backendPackId;
            const isExisting = packId && !String(packId).startsWith('group-');
            let backendPackId = isExisting ? packId : null;
            let success = false;

            if (isExisting) {
              const res = await testService.updateTestPack(packId, payload);
              if (res && res.status === 'success') {
                success = true;
              }
            } else {
              const res = await testService.saveTestPack(payload);
              if (res && res.status === 'success' && res.id) {
                backendPackId = res.id;
                success = true;
              }
            }

            if (success && backendPackId) {
              await testService.publishTestPack(backendPackId);
              draftGroups.push({
                ...g,
                status: 'Published',
                backendPackId,
                id: backendPackId
              });
            } else {
              draftGroups.push({
                ...g,
                status: 'Published'
              });
            }
          } catch (err) {
            console.error('Failed to sync/publish group to backend:', err);
            draftGroups.push({
              ...g,
              status: 'Published'
            });
          }
        }
      } else {
        // Fallback or local-only groups
        draft.groups?.forEach(g => {
          draftGroups.push({
            ...g,
            status: 'Published'
          });
        });
      }

      // 3. Normalize wizard field names to DB field names
      const publishedAssessment = {
        ...draft,
        groups: draftGroups,
        status: 'Published',
        updatedAt: new Date().toISOString(),
        backendId: backendId || draft.backendDraftId || draft.backendId,
        // Video — DB shape
        add_video: draft.videoEnabled ? 'Yes' : 'No',
        intro_videos: Array.isArray(updatedVideoFiles) ? updatedVideoFiles : (Array.isArray(draft.videoFiles) ? draft.videoFiles : []),
        // Exam config — DB shape
        allow_backtracking: draft.navigationControl ? 1 : 0,
        proctored_exam:     draft.fullscreenMode    ? 1 : 0,
        browser_lockdown:   draft.tabSwitchWarning  ? 1 : 0,
        show_results:       draft.autoSubmit        ? 1 : 0,
      };
      
      delete publishedAssessment.isEditMode;

      // 4. Save to published_tests in localStorage
      const existingPubStr = localStorage.getItem('published_tests');
      let publishedList = [];
      if (existingPubStr) {
        try {
          publishedList = JSON.parse(existingPubStr);
        } catch (e) {}
      }

      const existingIndex = publishedList.findIndex(t => String(t.id) === String(publishedAssessment.id));
      if (existingIndex > -1) {
        publishedList[existingIndex] = publishedAssessment;
      } else {
        publishedList.push(publishedAssessment);
      }
      localStorage.setItem('published_tests', JSON.stringify(publishedList));

      // 5. Clear the draft
      localStorage.removeItem('draft_assessment');

      // Clear from saved_drafts list as well to be safe
      if (draft && draft.id) {
        const savedDraftsKey = 'saved_drafts';
        const savedDraftsStrActual = localStorage.getItem(savedDraftsKey);
        if (savedDraftsStrActual) {
          try {
            const savedDrafts = JSON.parse(savedDraftsStrActual);
            const filtered = savedDrafts.filter(d => String(d.id) !== String(draft.id) && d.name !== draft.name);
            localStorage.setItem(savedDraftsKey, JSON.stringify(filtered));
          } catch (e) {}
        }
      }

      window.dispatchEvent(new Event('draft-updated'));
      toast.dismiss(publishingToast);
      toast.success("Test published successfully.");

      localStorage.setItem('assessment_active_module', 'inventory');
      navigate('/assessment');
    } catch (error) {
      toast.dismiss(publishingToast);
      console.error('Failed to publish assessment:', error);
      toast.error(error.message || 'Failed to publish test');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset draft handler
  const handleResetDraft = () => {
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Reset draft test?',
      message: 'Are you sure you want to completely discard this draft? This will clear all details and return you to the dashboard.',
      confirmText: 'Yes, discard it!',
      onConfirm: () => {
        localStorage.removeItem('draft_assessment');
        if (draft && draft.id) {
          const savedDraftsStr = localStorage.getItem('saved_drafts');
          if (savedDraftsStr) {
            try {
              const savedDrafts = JSON.parse(savedDraftsStr);
              const filtered = savedDrafts.filter(d => d.id !== draft.id);
              localStorage.setItem('saved_drafts', JSON.stringify(filtered));
            } catch (e) {}
          }
        }
        window.dispatchEvent(new Event('draft-updated'));
        setPopupConfig({ isOpen: false });
        toast.success('Draft test discarded.');
        localStorage.setItem('assessment_active_module', 'inventory');
        navigate('/assessment');
      }
    });
  };

  if (showSuccess) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-500">
        <div className="w-24 h-24 bg-emerald-50 border-4 border-emerald-500/20 rounded-full flex items-center justify-center text-emerald-500 shadow-2xl mb-8 animate-bounce">
          <FiCheckCircle className="text-5xl" />
        </div>
        <h2 className="text-3xl font-extrabold text-slate-800 tracking-tight mb-3">Test Saved Successfully!</h2>
        <p className="text-slate-500 font-bold max-w-md uppercase tracking-wider text-xs leading-relaxed">
          Redirecting back to dashboard in a moment...
        </p>
      </div>
    );
  }

  return (
    <div className="assess-mod-page p-4 w-full max-w-none flex flex-col h-full min-h-0 overflow-hidden relative">
      {/* Wizard Header / Brand bar */}
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">Test Setup Flow</h2>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={handleWizardBack}
            className="btn-assessment btn-assessment-danger flex items-center gap-2"
          >
            <FiArrowLeft />
            <span>Back</span>
          </button>
        </div>
      </div>

      {/* 5-Step Stepper Header */}
      <div className="bg-white border border-slate-100 rounded-2xl py-3 px-6 shadow-xl shadow-slate-100/30 mb-3 overflow-x-auto shrink-0">
          <div className="flex items-center justify-between min-w-[700px] px-4">
            {STEPS.map((s, idx) => {
              const isCurrent = step === s.id;
              const isCompleted = draft.completedSteps.includes(s.id);
              
              return (
                <React.Fragment key={s.id}>
                  {/* Step Item */}
                  <div 
                    onClick={() => handleStepClick(s)}
                    className={`flex items-center gap-3 select-none cursor-pointer transition-all duration-300 ${
                      isCurrent ? 'scale-105' : ''
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs border transition-all duration-300 ${
                      isCurrent 
                        ? 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-200' 
                        : isCompleted 
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                          : 'bg-slate-50 border-slate-100 text-slate-400'
                    }`}>
                      {isCompleted ? <FiCheck className="text-sm stroke-[3]" /> : idx + 1}
                    </div>
                    <div>
                      <span className={`block text-xs font-bold uppercase tracking-wider ${
                        isCurrent 
                          ? 'text-red-600' 
                          : isCompleted 
                            ? 'text-slate-800' 
                            : 'text-slate-400'
                      }`}>
                        {s.label}
                      </span>
                      <span className="block text-[8px] font-black text-slate-400 uppercase tracking-widest">
                        {isCurrent ? 'Current Step' : isCompleted ? 'Completed' : 'Upcoming'}
                      </span>
                    </div>
                  </div>

                  {/* Line Separator */}
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-4 transition-all duration-500 ${
                      isCompleted ? 'bg-emerald-200' : 'bg-slate-100'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

      {/* Wizard Step Content Area */}
      <div className="flex-1 min-h-0 overflow-hidden mb-0 flex flex-col">
        
        {/* STEP 1: CREATE TEST */}
        {step === 'create' && (
          <div className="bg-white border border-slate-100 rounded-3xl p-8 pb-24 shadow-xl shadow-slate-100/30 animate-in fade-in duration-300 max-h-full overflow-y-auto custom-scrollbar">
            <h3 className="text-lg font-extrabold text-slate-800 tracking-tight mb-6">
              Create New Test
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start mb-6">
              <div className="lg:col-span-3">
                <label className="block text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2">Test Name *</label>
                <input
                  ref={testNameInputRef}
                  type="text"
                  value={draft.name || ''}
                  onChange={(e) => updateDraft({ name: e.target.value })}
                  className="w-full h-11 bg-slate-50 border border-slate-100 rounded-xl px-4 text-sm font-semibold focus:border-red-500 focus:bg-white outline-none transition-all shadow-inner"
                  placeholder="e.g. Technical Proficiency Test"
                />
              </div>

              <div className="lg:col-span-2">
                <label className="block text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2">Test Category *</label>
                <select
                  value={draft.category || 'Internal'}
                  onChange={(e) => updateDraft({ category: e.target.value })}
                  className="w-full h-11 bg-slate-50 border border-slate-100 rounded-xl px-3 text-sm font-semibold focus:border-red-500 focus:bg-white outline-none transition-all shadow-inner"
                >
                  {categoriesList.length > 0 ? (
                    categoriesList.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))
                  ) : (
                    <>
                      <option value="Internal">Internal</option>
                      <option value="External">External</option>
                    </>
                  )}
                </select>
              </div>

              <div className="lg:col-span-2">
                <label className="block text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2">Test Type *</label>
                <select
                  value={draft.type || ''}
                  onChange={(e) => updateDraft({ type: e.target.value })}
                  className="w-full h-11 bg-slate-50 border border-slate-100 rounded-xl px-3 text-sm font-semibold focus:border-red-500 focus:bg-white outline-none transition-all shadow-inner"
                >
                  <option value="">Select type...</option>
                  <option value="Technical">Technical</option>
                  <option value="Compliance">Compliance</option>
                  <option value="Behavioral">Behavioral</option>
                </select>
              </div>

              <div className="lg:col-span-2">
                <label className="block text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2">Does this test include a video? *</label>
                <select
                  value={draft.videoEnabled ? 'Yes' : 'No'}
                  onChange={(e) => {
                    const enabled = e.target.value === 'Yes';
                    updateDraft({
                      videoEnabled: enabled,
                      videoFiles: enabled ? draft.videoFiles : []
                    });
                  }}
                  className={`w-full h-11 bg-slate-50 border rounded-xl px-3 text-sm font-semibold focus:bg-white outline-none transition-all shadow-inner ${
                    draft.videoEnabled ? 'border-red-500 focus:ring-1 focus:ring-red-500' : 'border-slate-100 focus:border-red-500'
                  }`}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>

              {draft.videoEnabled && (
                <div className="lg:col-span-3 flex flex-col">
                  <label className="block text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2">Upload Video *</label>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-11 bg-slate-50 border border-dashed border-red-200 rounded-xl px-4 flex items-center justify-between cursor-pointer hover:bg-red-50/10 transition-all shadow-inner"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FiUploadCloud className="text-red-500 text-sm shrink-0" />
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide truncate">
                        {draft.videoFiles && draft.videoFiles.length > 0 
                          ? `${draft.videoFiles.length} VIDEO(S) SELECTED` 
                          : 'UPLOAD VIDEO (4MB AND BELOW)'}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="text-[10px] font-black text-red-500 uppercase tracking-widest shrink-0"
                    >
                      BROWSE
                    </button>
                    <input 
                      type="file"
                      ref={fileInputRef}
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 4 * 1024 * 1024) {
                            toast.error('Video must be 4MB or below');
                            e.target.value = '';
                            return;
                          }
                          const currentFiles = draft.videoFiles || [];
                          const alreadyHasName = currentFiles.some(f => {
                            const n = typeof f === 'object' && f ? f.name : f;
                            return n === file.name;
                          });
                          if (alreadyHasName) {
                            toast.error('This video is already selected');
                            e.target.value = '';
                            return;
                          }
                          // Store File object in window cache (non-persisted memory) to prevent localStorage quota limit errors
                          window.selectedVideoFiles = window.selectedVideoFiles || {};
                          window.selectedVideoFiles[file.name] = file;

                          const objectUrl = URL.createObjectURL(file);
                          const newFileMeta = {
                            name: file.name,
                            size: file.size,
                            type: file.type,
                            objectUrl: objectUrl
                          };
                          updateDraft({ videoFiles: [...currentFiles, newFileMeta] });
                          e.target.value = '';
                        }
                      }}
                    />
                  </div>

                  {draft.videoFiles && draft.videoFiles.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      {draft.videoFiles.map((fileObj, index) => {
                        const displayName = typeof fileObj === 'object' && fileObj ? fileObj.name : fileObj;
                        return (
                        <div key={index} className="bg-slate-50 border border-slate-100 rounded-xl p-3 flex items-center justify-between">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                              <FiPlayCircle className="text-sm fill-red-500/10" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-[10px] font-bold text-slate-700 truncate max-w-[110px]" title={displayName}>
                                {displayName}
                              </div>
                              <div className="text-[8px] font-black text-slate-400 uppercase tracking-wider mt-0.5">
                                READY
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const name = typeof fileObj === 'object' && fileObj ? fileObj.name : null;
                              if (name && window.selectedVideoFiles) {
                                delete window.selectedVideoFiles[name];
                              }
                              if (fileObj && fileObj.objectUrl) {
                                try {
                                  URL.revokeObjectURL(fileObj.objectUrl);
                                } catch (err) {}
                              }
                              const updated = draft.videoFiles.filter((_, i) => i !== index);
                              updateDraft({ videoFiles: updated });
                            }}
                            className="text-slate-400 hover:text-red-500 transition-all p-1 shrink-0"
                          >
                            <FiTrash2 className="text-xs" />
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="flex flex-col h-full">
                <label className="block text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2">Instructions for the Test Takers *</label>
                <textarea
                  value={draft.instructions || ''}
                  onChange={(e) => updateDraft({ instructions: e.target.value })}
                  className="w-full flex-1 bg-slate-50 border border-slate-100 rounded-2xl p-4 text-sm font-semibold focus:border-red-500 focus:bg-white outline-none resize-none transition-all shadow-inner min-h-[160px]"
                  placeholder="Briefly provide instructions..."
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2">Exam Settings</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Navigation Control */}
                  <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-red-500 shrink-0">
                        <FiSkipBack className="text-md" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-black text-[#1e293b] leading-tight">Allow users to move between questions</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateDraft({ navigationControl: !draft.navigationControl })}
                      className={`w-10 h-5 flex items-center rounded-full p-0.5 cursor-pointer transition-all duration-300 shrink-0 ${
                        draft.navigationControl ? 'bg-red-500' : 'bg-slate-200'
                      }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-all duration-300 ${
                          draft.navigationControl ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Auto Submit */}
                  <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-red-500 shrink-0">
                        <FiSave className="text-md" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-black text-[#1e293b] leading-tight">Submit automatically when the time ends</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateDraft({ autoSubmit: !draft.autoSubmit })}
                      className={`w-10 h-5 flex items-center rounded-full p-0.5 cursor-pointer transition-all duration-300 shrink-0 ${
                        draft.autoSubmit ? 'bg-red-500' : 'bg-slate-200'
                      }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-all duration-300 ${
                          draft.autoSubmit ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Fullscreen Mode */}
                  <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-red-500 shrink-0">
                        <FiMaximize className="text-md" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-black text-[#1e293b] leading-tight">Keep the exam in the full screen mode</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateDraft({ fullscreenMode: !draft.fullscreenMode })}
                      className={`w-10 h-5 flex items-center rounded-full p-0.5 cursor-pointer transition-all duration-300 shrink-0 ${
                        draft.fullscreenMode ? 'bg-red-500' : 'bg-slate-200'
                      }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-all duration-300 ${
                          draft.fullscreenMode ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Tab Switch Warning */}
                  <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-red-500 shrink-0">
                        <FiAlertTriangle className="text-md" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-black text-[#1e293b] leading-tight">Show warning when leaving exam tab</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateDraft({ tabSwitchWarning: !draft.tabSwitchWarning })}
                      className={`w-10 h-5 flex items-center rounded-full p-0.5 cursor-pointer transition-all duration-300 shrink-0 ${
                        draft.tabSwitchWarning ? 'bg-red-500' : 'bg-slate-200'
                      }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-all duration-300 ${
                          draft.tabSwitchWarning ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEPS 2 & 3: Question Bank + Template (stacked — no unmount blink between steps) */}
        {(step === 'question-bank' || step === 'template') && (
          <div className="assess-wizard-template-stack flex-1 min-h-0 relative overflow-hidden flex flex-col">
            <div
              className={
                step === 'question-bank'
                  ? 'assess-wizard-template-stack__panel assess-wizard-template-stack__panel--active'
                  : 'assess-wizard-template-stack__panel assess-wizard-template-stack__panel--hidden'
              }
            >
              <QuestionBankManager
                isWizardMode={true}
                selectedQBId={draft.questionBank?.id || selectedQB?.id}
                draftQuestionBank={wizardDraftQuestionBank}
                onSelectQB={handleWizardSelectQB}
              />
            </div>
            {templatePanelReady && (
              <div
                className={
                  step === 'template'
                    ? 'assess-wizard-template-stack__panel assess-wizard-template-stack__panel--active'
                    : 'assess-wizard-template-stack__panel assess-wizard-template-stack__panel--hidden'
                }
              >
                <TemplateBuilder
                  isWizardMode={true}
                  isWizardStepActive={step === 'template'}
                  selectedTemplateId={wizardDraftTemplate?.id}
                  draftTemplate={wizardDraftTemplate}
                  wizardQuestionBank={wizardDraftQuestionBank}
                  onSelectTemplate={handleWizardSelectTemplate}
                  onWizardTemplateBridge={(api) => {
                    templateBridgeRef.current = api;
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* STEP 4: GROUPS & ASSIGN */}
        {step === 'groups' && (
          <div className="bg-white border border-slate-100 rounded-3xl p-8 pb-24 shadow-xl shadow-slate-100/30 animate-in fade-in duration-300 max-h-full overflow-y-auto custom-scrollbar">
            <div className="flex items-center justify-between border-b pb-6 mb-6">
              <div className="flex items-center gap-4">
                <span className="text-sm font-black text-slate-800 tracking-wider">GROUPS</span>
              </div>
              <button 
                onClick={() => {
                  if (editingGroupId || (showInputRow && newGroup.pack_name.trim() !== '')) {
                    toast.error('Please save or cancel the group you are editing first.');
                    return;
                  }
                  setNewGroup({
                    pack_name: '',
                    template_id: '',
                    template_name: '',
                    user_role: [],
                    candidates: [],
                    scheduled_date: new Date().toISOString().split('T')[0],
                    start_time: '10:00',
                    end_time: '11:00',
                    duration: 60,
                  });
                  setShowInputRow(true);
                }}
                className="px-4 py-2 bg-red-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-red-950 transition-all shadow-md"
              >
                <FiPlus />
                <span>Add Next Group</span>
              </button>
            </div>

            <div className="assess-mod-groups-panel-scroll">
              <div className="assess-mod-groups-panel">
                <table className="assess-mod-groups-accordion-table w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-name">Group Name</th>
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-template">Select Template</th>
                      {!isHrFresher && (
                        <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-roles">Assigned Roles</th>
                      )}
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-assign">
                        <div className="flex items-center gap-1.5">
                          <span>Assign To</span>
                          {isHrFresher && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleHeaderPlusClick('hrUpload');
                              }}
                              className="shrink-0 w-4 h-4 bg-red-800 text-white rounded-md flex items-center justify-center hover:bg-red-950 transition-colors shadow-sm"
                              title="External Setup Candidates Onboarding & Repository"
                              aria-label="External Setup Candidates Onboarding & Repository"
                            >
                              <FiPlus className="text-[8px] stroke-[2.5]" />
                            </button>
                          )}
                        </div>
                      </th>
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-date text-center">Test Date</th>
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-start text-center">Start Time</th>
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-end text-center">End Time</th>
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-duration text-center">Duration</th>
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-action text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <style dangerouslySetInnerHTML={{__html: `
                      input[type="date"]::-webkit-calendar-picker-indicator,
                      input[type="time"]::-webkit-calendar-picker-indicator {
                        background: transparent;
                        bottom: 0;
                        color: transparent;
                        cursor: pointer;
                        height: auto;
                        left: 0;
                        position: absolute;
                        right: 0;
                        top: 0;
                        width: auto;
                      }
                    `}} />
                    {(() => {
                      const renderInputRow = () => (
                      <tr className="border-b border-slate-100 bg-slate-50/30">
                        {/* GROUP NAME */}
                        <td className="py-3 pr-2 assess-mod-groups-col-name align-middle">
                          <input 
                            ref={groupNameInputRef}
                            type="text"
                            required
                            value={newGroup.pack_name}
                            onChange={(e) => setNewGroup({ ...newGroup, pack_name: e.target.value })}
                            placeholder="e.g. Morning Group"
                            className="w-full bg-[#fcfdfe] border border-red-400 focus:ring-4 focus:ring-rose-50 rounded-xl px-4 py-2.5 text-sm font-bold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 shadow-sm"
                          />
                        </td>
                        
                        {/* SELECT TEMPLATE */}
                        <td className="py-3 pr-2 assess-mod-groups-col-template align-middle">
                          <div className="relative w-full">
                            <select
                              value={newGroup.template_id || (draft.template?.id || '')}
                              onChange={(e) => {
                                const selectedTpl = templates.find(t => String(t.id) === String(e.target.value));
                                setNewGroup({
                                  ...newGroup,
                                  template_id: e.target.value,
                                  template_name: selectedTpl ? selectedTpl.name : ''
                                });
                              }}
                              className="w-full bg-[#fcfdfe] border border-slate-100 rounded-xl pl-4 pr-10 py-2.5 text-sm font-bold text-[#1e293b] outline-none transition-all appearance-none shadow-sm focus:ring-4 focus:ring-rose-50"
                            >
                              <option value="">Select Template</option>
                              {templates.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <FiChevronDown className="text-slate-400 text-sm" />
                            </div>
                          </div>
                        </td>

                        {/* ASSIGNED ROLES */}
                        {!isHrFresher && (
                          <td className="py-3 pr-2 assess-mod-groups-col-roles align-middle">
                            {(() => {
                              const selectedRoleNames = resolveRoleNames(newGroup.user_role);
                              const selectedRoleNamesText = selectedRoleNames.join(', ');
                              return (
                            <div className="assess-mod-groups-picker-wrap" ref={groupRolesDropdownRef}>
                              <GroupsInlineSearchField
                                value={roleSearchTerm}
                                onChange={(e) => {
                                  setRoleSearchTerm(e.target.value);
                                  setShowSelectedRolesOnly(false);
                                  setShowRolesDropdown(true);
                                }}
                                onFocus={() => {
                                  setShowSelectedRolesOnly(false);
                                  setShowRolesDropdown(true);
                                }}
                                placeholder="Search roles..."
                                dropdownOpen={showRolesDropdown}
                                onToggleDropdown={() => {
                                  setShowSelectedRolesOnly(false);
                                  setShowRolesDropdown((open) => !open);
                                }}
                                selectionSummary={
                                  newGroup.user_role.length > 0 ? (
                                    <RoleSelectionSummary
                                      names={selectedRoleNames}
                                      totalCount={getRolesForForm().length}
                                      summaryMode="count"
                                    />
                                  ) : null
                                }
                                selectionSummaryTitle={selectedRoleNamesText}
                                toggleAriaLabel="Toggle roles list"
                                showViewSelected={selectedRoleNames.length > 0}
                                onViewSelected={() => {
                                  setRoleSearchTerm('');
                                  setShowSelectedRolesOnly(true);
                                  setShowRolesDropdown(true);
                                }}
                                viewSelectedTooltip={`View selected roles: ${selectedRoleNamesText}`}
                              />
                              <GroupsPickerDropdownPortal
                                ref={groupRolesPanelRef}
                                isOpen={showRolesDropdown}
                                anchorRef={groupRolesDropdownRef}
                                minWidth={288}
                                title="Assigned Roles"
                                subtitle={showSelectedRolesOnly ? 'Selected Role List' : 'Role List'}
                                count={newGroup.user_role.length}
                              >
                                <div className="assess-mod-groups-member-panel__list">
                                  {!showSelectedRolesOnly && (
                                    <GroupsPickerMemberRow
                                      isSelectAll
                                      selectAllLabel="Select All"
                                      checked={
                                        visibleGroupRoles.length > 0 &&
                                        visibleGroupRoles.every((r) =>
                                          newGroup.user_role.map(String).includes(String(r.id))
                                        )
                                      }
                                      onChange={(e) => selectAllGroupRoles(e.target.checked)}
                                    />
                                  )}
                                  {visibleGroupRoles.map((role) => (
                                    <GroupsPickerMemberRow
                                      key={role.id}
                                      name={role.name}
                                      checked={newGroup.user_role.map(String).includes(String(role.id))}
                                      onChange={() => toggleGroupRole(role.id)}
                                    />
                                  ))}
                                </div>
                                {visibleGroupRoles.length === 0 && (
                                  <p className="assess-mod-groups-member-panel__empty">
                                    {showSelectedRolesOnly ? 'No selected roles' : 'No roles found'}
                                  </p>
                                )}
                              </GroupsPickerDropdownPortal>
                            </div>
                              );
                            })()}
                          </td>
                        )}

                        {/* ASSIGN TO */}
                        <td className="py-3 pr-2 assess-mod-groups-col-assign align-middle">
                          {isHrFresher ? (
                            <div className="relative w-full">
                              <select
                                value={newGroup.user_role[0] || ''}
                                onChange={(e) => {
                                  const selectedCollegeName = e.target.value;
                                  const templateObj = collegeTemplates.find(t => t.name === selectedCollegeName);
                                  setNewGroup({
                                    ...newGroup,
                                    user_role: selectedCollegeName ? [selectedCollegeName] : [],
                                    candidates: templateObj ? templateObj.student_ids : []
                                  });
                                }}
                                className="w-full bg-[#fcfdfe] border border-slate-100 rounded-xl pl-4 pr-10 py-2.5 text-sm font-bold text-[#1e293b] outline-none transition-all appearance-none shadow-sm focus:ring-4 focus:ring-rose-50"
                              >
                                <option value="">Select College Name</option>
                                {collegeTemplates.map(t => (
                                  <option key={t.id} value={t.name}>{t.name}</option>
                                ))}
                              </select>
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <FiChevronDown className="text-slate-400 text-sm" />
                              </div>
                            </div>
                          ) : (
                            <div className="assess-mod-groups-picker-wrap" ref={dropdownRef}>
                              {(() => {
                                const selectedCandidateNames = newGroup.candidates.map((id) => {
                                  const emp = candidatesList.find((e) => String(e.id) === String(id));
                                  return emp ? (emp.first_name || emp.name || String(id)) : String(id);
                                });
                                const selectedCandidateNamesText = selectedCandidateNames.join(', ');
                                return (
                                  <>
                              <GroupsInlineSearchField
                                value={candidateSearchTerm}
                                onChange={(e) => {
                                  setCandidateSearchTerm(e.target.value);
                                  setShowSelectedCandidatesOnly(false);
                                  setShowCandidatesDropdown(true);
                                }}
                                onFocus={() => {
                                  if (!isHrFresher && newGroup.user_role.length === 0) return;
                                  setShowSelectedCandidatesOnly(false);
                                  setShowCandidatesDropdown(true);
                                }}
                                placeholder={
                                  isHrFresher
                                    ? 'Search candidates...'
                                    : (newGroup.user_role.length === 0 ? 'Select roles first' : 'Search candidates...')
                                }
                                disabled={!isHrFresher && newGroup.user_role.length === 0}
                                dropdownOpen={showCandidatesDropdown}
                                onToggleDropdown={() => {
                                  if (!isHrFresher && newGroup.user_role.length === 0) {
                                    toast.error('Please select roles first');
                                    return;
                                  }
                                  setShowSelectedCandidatesOnly(false);
                                  setShowCandidatesDropdown((open) => !open);
                                }}
                                selectionSummary={
                                  newGroup.candidates.length > 0 ? (
                                    <RoleSelectionSummary
                                      names={selectedCandidateNames}
                                      summaryMode="count"
                                      itemLabel="candidates"
                                    />
                                  ) : null
                                }
                                selectionSummaryTitle={selectedCandidateNamesText}
                                toggleAriaLabel="Toggle candidates list"
                                showViewSelected={selectedCandidateNames.length > 0}
                                onViewSelected={() => {
                                  setCandidateSearchTerm('');
                                  setShowSelectedCandidatesOnly(true);
                                  setShowCandidatesDropdown(true);
                                }}
                                viewSelectedTooltip={`View selected candidates: ${selectedCandidateNamesText}`}
                              />
                              <GroupsPickerDropdownPortal
                                ref={employeeDropdownPanelRef}
                                isOpen={showCandidatesDropdown}
                                anchorRef={dropdownRef}
                                minWidth={256}
                                title="Group Members"
                                subtitle={showSelectedCandidatesOnly ? 'Selected Candidate List' : 'Candidate List'}
                                count={newGroup.candidates.length}
                              >
                                <div className="assess-mod-groups-member-panel__list">
                                  {!showSelectedCandidatesOnly && (
                                    <GroupsPickerMemberRow
                                      isSelectAll
                                      selectAllLabel="Select All Members"
                                      checked={(() => {
                                        const visible = getVisibleEmployees();
                                        return (
                                          visible.length > 0 &&
                                          visible.every((emp) =>
                                            newGroup.candidates.map(String).includes(String(emp.id))
                                          )
                                        );
                                      })()}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        const visible = getVisibleEmployees();
                                        const visibleIds = visible.map((emp) => String(emp.id));
                                        const current = Array.isArray(newGroup.candidates)
                                          ? newGroup.candidates.map(String)
                                          : [];
                                        const updated = checked
                                          ? [...new Set([...current, ...visibleIds])]
                                          : current.filter((id) => !visibleIds.includes(id));
                                        setNewGroup({ ...newGroup, candidates: updated });
                                      }}
                                    />
                                  )}
                                  {loadingCandidates ? (
                                    <div className="py-4 text-center">
                                      <div className="w-5 h-5 border border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-1" />
                                      <span className="text-[8px] font-black text-slate-400">Loading...</span>
                                    </div>
                                  ) : (
                                    getVisibleEmployees().map((emp) => (
                                      <GroupsPickerMemberRow
                                        key={emp.id}
                                        name={emp.first_name || emp.name || ''}
                                        meta={emp.email}
                                        checked={newGroup.candidates.map(String).includes(String(emp.id))}
                                        onChange={() => toggleEmployee(emp.id)}
                                      />
                                    ))
                                  )}
                                </div>
                                {!loadingCandidates && getVisibleEmployees().length === 0 && (
                                  <p className="assess-mod-groups-member-panel__empty">
                                    {showSelectedCandidatesOnly ? 'No selected candidates' : 'No candidates found'}
                                  </p>
                                )}
                              </GroupsPickerDropdownPortal>
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </td>

                        {/* DATE */}
                        <td className="py-3 pr-2 assess-mod-groups-col-date align-middle">
                          <div className="relative flex items-center justify-between bg-white border border-slate-200 rounded-xl px-3 py-1.5 shadow-sm hover:border-slate-300 transition-all w-full max-w-[150px] min-w-[130px] h-[36px]">
                            <span className="text-xs font-bold text-slate-700 tracking-wide select-none">
                              {formatDateToDDMMYYYY(newGroup.scheduled_date) || 'Select Date'}
                            </span>
                            <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <input 
                              type="date"
                              required
                              ref={dateInputRef}
                              className="assess-mod-groups-datetime-input absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              value={newGroup.scheduled_date}
                              onChange={(e) => setNewGroup({ ...newGroup, scheduled_date: e.target.value })}
                            />
                          </div>
                        </td>

                        {/* START TIME */}
                        <td className="py-3 pr-2 assess-mod-groups-col-start align-middle">
                          <TimePicker12Hour
                            value={newGroup.start_time}
                            onChange={(val) => handleTimeFieldChange('start_time', val)}
                          />
                        </td>

                        {/* END TIME */}
                        <td className="py-3 pr-2 assess-mod-groups-col-end align-middle">
                          <TimePicker12Hour
                            value={newGroup.end_time}
                            onChange={(val) => handleTimeFieldChange('end_time', val)}
                          />
                        </td>

                        {/* DURATION */}
                        <td className="py-3 pr-2 assess-mod-groups-col-duration align-middle">
                          <div className="assess-mod-groups-duration-wrap">
                            <input 
                              type="number"
                              readOnly
                              value={newGroup.duration}
                              className="bg-transparent border-0 focus:ring-0 outline-none"
                            />
                            <span className="text-xs font-bold text-slate-400">M</span>
                          </div>
                        </td>

                        {/* ACTION */}
                        <td className="py-3 assess-mod-groups-col-action align-middle">
                          <div className="assess-mod-groups-action-wrap">
                            <button 
                              type="button"
                              onClick={handleAddGroupSubmit}
                              className="assess-mod-groups-action-btn bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white group"
                              title="Save Group"
                            >
                              <FiSave className="group-hover:scale-110 transition-transform" />
                            </button>
                            {(editingGroupId || (draft.groups && draft.groups.length > 0)) && (
                              <button 
                                type="button"
                                onClick={() => {
                                  setShowInputRow(false);
                                  setEditingGroupId(null);
                                  setNewGroup(getDefaultNewGroup());
                                  setRoleSearchTerm('');
                                  setCandidateSearchTerm('');
                                  clearPendingGroupForm();
                                }}
                                className="assess-mod-groups-action-btn assess-mod-inventory-action-btn assess-mod-inventory-action-btn--red group"
                                title="Cancel"
                              >
                                <FiTrash2 className="group-hover:scale-110 transition-transform" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                      
                      return (
                        <>
                          {draft.groups?.map(g => {
                            if (editingGroupId === g.id) return renderInputRow();
                            const resolvedRoles = resolveRoleNames(g.user_role);
                            const resolvedRolesText = resolvedRoles.join(', ');
                            const candidateIds = String(g.candidates || '').split(',').filter(Boolean);
                            const candCount = candidateIds.length;
                            return (
                              <React.Fragment key={g.id}>
                                <tr className="border-b border-slate-50 transition-colors hover:bg-slate-50/30">
                                  <td className="py-4 font-bold text-slate-700 text-sm assess-mod-groups-col-name">{g.pack_name}</td>
                                  <td className="py-4 text-xs font-semibold text-slate-500 assess-mod-groups-col-template">{g.template_name}</td>
                                  {!isHrFresher && (
                                    <td className="py-4 text-xs assess-mod-groups-col-roles">
                                      <GroupsMemberPreviewPopover
                                        title="Assigned Roles"
                                        subtitle="Role List"
                                        badgeCount={resolvedRoles.length}
                                        trigger={
                                          <div className="flex items-center gap-1.5 font-bold text-slate-700 hover:text-slate-900 transition-colors cursor-help group select-none">
                                            <span className="max-w-[200px] truncate">
                                              <RoleSelectionSummary names={resolvedRoles} placeholder="N/A" />
                                            </span>
                                            <FiChevronDown className="text-slate-400 text-xs transition-transform duration-200 group-hover:rotate-180" />
                                          </div>
                                        }
                                      >
                                        {resolvedRoles.length === 0 ? (
                                          <div className="p-3 text-xs text-slate-400 italic">No roles assigned</div>
                                        ) : (
                                          resolvedRoles.map((role, idx) => (
                                            <GroupsPickerPreviewRow
                                              key={idx}
                                              name={role}
                                            />
                                          ))
                                        )}
                                      </GroupsMemberPreviewPopover>
                                    </td>
                                  )}
                                  <td className="py-4 text-xs assess-mod-groups-col-assign">
                                    <GroupsMemberPreviewPopover
                                      title="Assigned Candidates"
                                      subtitle={isHrFresher ? 'External Setup Groups' : 'Candidate List'}
                                      badgeCount={candCount}
                                      trigger={
                                        <div className="flex items-center gap-1.5 font-bold text-slate-700 hover:text-slate-900 transition-colors cursor-help group select-none">
                                          <FiUsers className="text-slate-400 text-xs shrink-0" />
                                          <span>{candCount} Candidates</span>
                                          <FiChevronDown className="text-slate-400 text-xs transition-transform duration-200 group-hover:rotate-180" />
                                        </div>
                                      }
                                    >
                                      {candCount === 0 ? (
                                        <div className="p-3 text-xs text-slate-400 italic">No candidates assigned</div>
                                      ) : (
                                        candidateIds.map((cid, idx) => {
                                          const emp = resolvedCandidatesMap[String(cid)];
                                          const name = emp ? (emp.first_name || emp.name || `ID: ${cid}`) : `ID: ${cid}`;
                                          const email = emp?.email || '';
                                          return (
                                            <GroupsPickerPreviewRow
                                              key={idx}
                                              name={name}
                                              meta={email}
                                            />
                                          );
                                        })
                                      )}
                                    </GroupsMemberPreviewPopover>
                                  </td>
                                  <td className="py-4 text-xs font-semibold text-slate-500 assess-mod-groups-col-date text-center">{formatDateToDDMMYYYY(g.scheduled_date)}</td>
                                  <td className="py-4 text-xs font-semibold text-slate-500 assess-mod-groups-col-start text-center">{formatTime12Hour(g.start_time)}</td>
                                  <td className="py-4 text-xs font-semibold text-slate-500 assess-mod-groups-col-end text-center">{formatTime12Hour(g.end_time)}</td>
                                  <td className="py-4 text-xs font-black text-slate-700 assess-mod-groups-col-duration text-center">{g.duration} Mins</td>
                                  <td className="py-4 assess-mod-groups-col-action text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <button 
                                        type="button"
                                        onClick={() => handleEditGroup(g)}
                                        className="w-8 h-8 rounded-lg bg-blue-50 hover:bg-blue-500 text-blue-500 hover:text-white flex items-center justify-center transition-all border border-blue-100 shadow-sm"
                                        title="Edit Group"
                                      >
                                        <FiEdit className="text-xs" />
                                      </button>
                                      <button 
                                        type="button"
                                        onClick={() => handleDeleteGroup(g.id)}
                                        className="w-8 h-8 rounded-lg bg-rose-50 hover:bg-rose-500 text-rose-500 hover:text-white flex items-center justify-center transition-all border border-rose-100 shadow-sm"
                                        title="Delete Group"
                                      >
                                        <FiTrash2 className="text-xs" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          })}
                          {(!editingGroupId && (showInputRow || !draft.groups || draft.groups.length === 0)) && renderInputRow()}
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* STEP 5: REVIEW & PUBLISH */}
        {step === 'publish' && (
          <div className="assess-wizard-review-page assess-wizard-review-page--with-actions animate-in fade-in duration-300 custom-scrollbar">
            <h3 className="assess-wizard-review-title shrink-0">
              Review & Publish
            </h3>

            <div className="assess-wizard-review-summary shrink-0">
              <div className="assess-wizard-review-summary-stack">
                <div className="assess-wizard-review-summary-col">
                  <h3 className="assess-wizard-review-card-title">
                    <FiFileText className="text-red-500 text-sm" />
                    Test Information
                  </h3>
                  <div className="assess-wizard-review-rows">
                    <ReviewDetailRow label="Test Name" value={draft.name} />
                    <ReviewDetailRow label="Category" value={draft.category} />
                    <ReviewDetailRow label="Type" value={draft.type} />
                    <ReviewDetailRow label="Instructions for the Test Takers" value={draft.instructions} multiline />
                    <ReviewDetailRow label="Does this test include a video" value={draft.videoEnabled ? 'Yes' : 'No'} />
                  </div>
                </div>

                <div className="assess-wizard-review-summary-col">
                  <h3 className="assess-wizard-review-card-title">
                    <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                    </svg>
                    Question Bank
                  </h3>
                  <div className="assess-wizard-review-rows">
                    <ReviewDetailRow label="Question Bank Name" value={draft.questionBank?.name} />
                    <ReviewDetailRow label="Total Questions" value={getQuestionBankCount(draft.questionBank)} />
                    <ReviewDetailRow
                      label="Question Type(s)"
                      value={
                        Array.from(new Set((draft.questionBank?.questions || []).map(q => q.marks_type || q.type || 'MCQ'))).filter(Boolean).join(', ') || 'MCQ'
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="assess-wizard-review-summary-col">
                <h3 className="assess-wizard-review-card-title">
                  <FiLayers className="text-red-500 text-sm" />
                  Structure & Scoring
                </h3>
                <div className="assess-wizard-review-rows">
                  <ReviewDetailRow label="Template Name" value={draft.template?.name} />
                  <ReviewDetailRow label="Pass Mark" value={`${getTemplatePassMark(draft.template)} Marks`} />
                  <ReviewDetailRow
                    label="Total Mark"
                    value={`${getTemplateTotalMarks(draft.template)} Marks`}
                  />
                </div>

                <div className="assess-wizard-review-sections">
                  <span className="assess-wizard-review-sections-label">Sections Configured</span>
                  <div className="assess-wizard-review-section-list">
                    {normalizeTemplateSections(draft.template?.sections).map((sec, idx) => (
                      <div key={idx} className="assess-wizard-review-section-item">
                        <span>{sec.name}</span>
                        <span className="assess-wizard-review-section-meta">
                          {sec.count} Qs · {sec.marks}M each
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="assess-wizard-review-card assess-wizard-review-cohorts">
              <h3 className="assess-wizard-review-card-title assess-wizard-review-card-title--tight">
                <FiUsers className="text-red-500 text-sm" />
                Assigned Groups & Schedule
              </h3>

              <div className="assess-wizard-review-table-wrap">
                <div className="overflow-x-auto">
                  <table className="assess-wizard-review-table">
                    <thead>
                      <tr>
                        <th>Group</th>
                        <th>Role</th>
                        <th className="text-center">Candidates</th>
                        <th className="text-center">Date & Time</th>
                        <th className="text-center">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draft.groups?.map(g => {
                        const rolesList = resolveRoleNames(g.user_role);
                        const candCount = String(g.candidates || '').split(',').filter(Boolean).length;
                        return (
                          <tr key={g.id}>
                            <td className="font-bold text-slate-700 text-sm">{g.pack_name}</td>
                            <td>
                              <div className="flex flex-wrap gap-1.5 justify-start">
                                {rolesList.length > 0 ? (
                                  rolesList.map((roleName, rIdx) => (
                                    <span key={rIdx} className="bg-red-50 text-red-600 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider">
                                      {roleName}
                                    </span>
                                  ))
                                ) : (
                                  <span className="bg-slate-50 text-slate-400 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider">
                                    N/A
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="text-xs font-bold text-slate-700 text-center">
                              {candCount} Candidates
                            </td>
                            <td className="text-xs font-semibold text-slate-500 text-center">
                              {formatDateToDDMMYYYY(g.scheduled_date)} at {formatTime12Hour(g.start_time)}
                            </td>
                            <td className="text-xs font-bold text-slate-700 text-center">
                              {g.duration} min
                            </td>
                          </tr>
                        );
                      })}
                      {(!draft.groups || draft.groups.length === 0) && (
                        <tr>
                          <td colSpan="5" className="py-10 text-center text-xs font-bold text-slate-400 uppercase tracking-wider">
                            No cohorts or groups assigned
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Shared wizard navigation bottom bar */}
      <div
        className={
          step === 'publish'
            ? 'assess-wizard-actions-bar--publish'
            : 'absolute bottom-4 right-4 z-50 flex justify-end items-center gap-3 pointer-events-none assess-wizard-actions-bar'
        }
      >
        <button 
          onClick={step === 'publish' ? handlePublishAssessment : handleSaveAndContinue}
          disabled={isSubmitting}
          className="btn-assessment btn-assessment-danger flex items-center gap-2 pointer-events-auto shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span>
            {step === 'publish'
              ? 'Publish Test'
              : ((step === 'groups' && editingGroupId)
                ? 'Update & Continue'
                : (draft?.isEditMode ? 'Update & Continue' : 'Save & Continue'))}
          </span>
          <FiArrowRight />
        </button>
        <button 
          onClick={handleSaveAndExit}
          disabled={isSubmitting}
          className="btn-assessment btn-assessment-secondary flex items-center gap-2 pointer-events-auto shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span>Save Draft & Exit</span>
        </button>
      </div>



      {/* Double click viewing question details popup */}
      {viewingQuestion && (
        <AssessmentPopup
          isOpen={true}
          title="View Question Details"
          type="info"
          onCancel={() => setViewingQuestion(null)}
          confirmText="Close"
          onConfirm={() => setViewingQuestion(null)}
          message={
            <div className="text-left space-y-4">
              <div className="border-b pb-2">
                <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Question</span>
                <p className="text-sm font-bold text-slate-800 mt-1 leading-relaxed">{viewingQuestion.question}</p>
              </div>
              {String(viewingQuestion.type).toUpperCase() === 'MCQ' && (
                <div className="grid grid-cols-2 gap-3">
                  {['a', 'b', 'c', 'd'].map(opt => {
                    const optText = viewingQuestion[`option_${opt}`] || viewingQuestion[`option_${opt.toUpperCase()}`];
                    const isCorrect = String(viewingQuestion.correct_answer).toUpperCase() === opt.toUpperCase();
                    return (
                      <div key={opt} className={`p-3 rounded-xl border ${isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-100'}`}>
                        <span className="text-[10px] font-black text-slate-400 uppercase">Option {opt.toUpperCase()}</span>
                        <p className="text-xs font-bold text-slate-700 mt-1 truncate">{optText || '—'}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Correct Answer</span>
                  <p className="text-xs font-bold text-emerald-600 mt-0.5">{viewingQuestion.correct_answer || '—'}</p>
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Marks</span>
                  <p className="text-xs font-bold text-slate-700 mt-0.5">{viewingQuestion.marks || '—'}</p>
                </div>
              </div>
            </div>
          }
        />
      )}

      {/* Confirmation reset popup */}
      <AssessmentPopup
        {...popupConfig}
        onCancel={() => setPopupConfig({ ...popupConfig, isOpen: false })}
      />
    </div>
  );
};

export const BatchWizard = ({ isOpen, onClose, assessment }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full">
        <h3 className="text-xl font-bold mb-4">Group Flow</h3>
        <p className="text-gray-600 mb-6">Initializing group for: <strong>{assessment?.name}</strong></p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn-assessment btn-assessment-secondary">Close</button>
          <button className="btn-assessment btn-assessment-danger">Initialize</button>
        </div>
      </div>
    </div>
  );
};

export default BatchWizard;
